import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/client";
import { downloadAuthed } from "../utils/download";
import { PRODUCT_NAME, useBranding } from "../context/BrandingContext";

type Group = "Start here" | "How do I…" | "Configure" | "Reference" | "Help";

type Section = { id: string; label: string; group: Group; icon?: string };

const GROUP_STYLE: Record<
  Group,
  { dot: string; chip: string; chipText: string; accent: string; activeBg: string }
> = {
  "Start here": {
    dot: "bg-blue-600",
    chip: "bg-blue-100 dark:bg-blue-900/40",
    chipText: "text-blue-700 dark:text-blue-300",
    accent: "bg-blue-600",
    activeBg: "bg-blue-600",
  },
  "How do I…": {
    dot: "bg-indigo-600",
    chip: "bg-indigo-100 dark:bg-indigo-900/40",
    chipText: "text-indigo-700 dark:text-indigo-300",
    accent: "bg-indigo-600",
    activeBg: "bg-indigo-600",
  },
  Configure: {
    dot: "bg-amber-600",
    chip: "bg-amber-100 dark:bg-amber-900/40",
    chipText: "text-amber-800 dark:text-amber-300",
    accent: "bg-amber-600",
    activeBg: "bg-amber-600",
  },
  Reference: {
    dot: "bg-teal-600",
    chip: "bg-teal-100 dark:bg-teal-900/40",
    chipText: "text-teal-800 dark:text-teal-300",
    accent: "bg-teal-600",
    activeBg: "bg-teal-600",
  },
  Help: {
    dot: "bg-slate-600",
    chip: "bg-slate-200 dark:bg-slate-700",
    chipText: "text-slate-700 dark:text-slate-200",
    accent: "bg-slate-600",
    activeBg: "bg-slate-700",
  },
};

const SECTIONS: Section[] = [
  { id: "overview", label: "Welcome", group: "Start here", icon: "🚀" },
  { id: "tour", label: "Quick tour", group: "Start here", icon: "🗺️" },
  { id: "day-one", label: "Day 1: First-time setup", group: "Start here", icon: "🌅" },

  { id: "wf-add-customer", label: "Add a new customer", group: "How do I…", icon: "🏢" },
  { id: "wf-deploy-agent", label: "Deploy the agent", group: "How do I…", icon: "📦" },
  { id: "wf-watch-devices", label: "Watch devices come in", group: "How do I…", icon: "👀" },
  { id: "wf-triage-alerts", label: "Triage the morning alerts", group: "How do I…", icon: "☕" },
  { id: "wf-create-ticket", label: "Open & work a ticket", group: "How do I…", icon: "🎟️" },
  { id: "wf-sla-breach", label: "Handle an SLA breach", group: "How do I…", icon: "⏱️" },
  { id: "wf-run-script", label: "Run a script on a device", group: "How do I…", icon: "📜" },
  { id: "wf-remote-shell", label: "Open a remote shell", group: "How do I…", icon: "🖥️" },
  { id: "wf-approve-patches", label: "Approve patches", group: "How do I…", icon: "📦" },
  { id: "wf-reset-ad", label: "Reset an AD password", group: "How do I…", icon: "🔑" },
  { id: "wf-entra-users", label: "Manage a cloud (Entra) user", group: "How do I…", icon: "☁️" },
  { id: "wf-store-creds", label: "Store a customer credential", group: "How do I…", icon: "🔐" },
  { id: "wf-bulk-assign", label: "Bulk-assign devices", group: "How do I…", icon: "🗂️" },
  { id: "wf-build-report", label: "Build a custom report", group: "How do I…", icon: "📊" },
  { id: "wf-send-qbr", label: "Send a QBR to a customer", group: "How do I…", icon: "📈" },
  { id: "wf-compliance", label: "Generate compliance evidence", group: "How do I…", icon: "📄" },
  { id: "wf-attest", label: "Record a compliance attestation", group: "How do I…", icon: "✍️" },
  { id: "wf-sla-summary", label: "Send weekly SLA summary", group: "How do I…", icon: "📧" },
  { id: "wf-runbook", label: "Write & share a runbook", group: "How do I…", icon: "📘" },
  { id: "wf-onboarding-portal", label: "Onboard a portal user", group: "How do I…", icon: "👤" },
  { id: "wf-rollout-agent", label: "Roll out a new agent version", group: "How do I…", icon: "🚢" },
  { id: "wf-fix-autostart", label: "Fix agents that don't survive a reboot", group: "How do I…", icon: "🔁" },
  { id: "wf-webhook", label: "Wire up a Slack/Teams alert", group: "How do I…", icon: "🔔" },
  { id: "wf-2fa", label: "Set up MFA on my account", group: "How do I…", icon: "🛡️" },
  { id: "wf-sso", label: "Sign in with Entra / Okta / Google (SSO)", group: "How do I…", icon: "🔑" },
  { id: "wf-connect-azure", label: "Connect a customer's Azure subscription", group: "How do I…", icon: "☁️" },
  { id: "wf-connect-aws", label: "Connect a customer's AWS account", group: "How do I…", icon: "☁️" },
  { id: "wf-connect-gcp", label: "Connect a customer's GCP project", group: "How do I…", icon: "☁️" },
  { id: "wf-connect-gworkspace", label: "Connect a customer's Google Workspace", group: "How do I…", icon: "👥" },
  { id: "wf-ai-cloud-commands", label: "Drive cloud / Meraki / M365 from the AI", group: "How do I…", icon: "🤖" },
  { id: "wf-api-token", label: "Mint an API token", group: "How do I…", icon: "🗝️" },
  { id: "wf-push", label: "Enable push notifications", group: "How do I…", icon: "📲" },
  { id: "wf-spiceworks", label: "Migrate from Spiceworks", group: "How do I…", icon: "📥" },

  { id: "appliance-setup", label: "First-run setup wizard", group: "Configure", icon: "🧰" },
  { id: "appliance-license", label: "Appliance licensing", group: "Configure", icon: "🔑" },
  { id: "settings-when", label: "Settings: what to set & when", group: "Configure", icon: "⚙️" },
  { id: "thresholds", label: "Tuning alert thresholds", group: "Configure", icon: "🎚️" },

  { id: "ref-dashboard", label: "Dashboard", group: "Reference", icon: "📍" },
  { id: "ref-devices", label: "Devices", group: "Reference", icon: "💻" },
  { id: "ref-hw", label: "Hardware signals", group: "Reference", icon: "🌡️" },
  { id: "patch-policy", label: "Choosing a patch policy", group: "Configure", icon: "🧩" },
  { id: "ref-tickets", label: "Tickets", group: "Reference", icon: "🎫" },
  { id: "maintenance", label: "Maintenance windows", group: "Configure", icon: "🌙" },
  { id: "sla-policy", label: "SLA policy", group: "Configure", icon: "📋" },
  { id: "sso-providers", label: "SSO providers (Entra / Okta / Google)", group: "Configure", icon: "🔑" },
  { id: "cloud-connectors", label: "Cloud connectors (Azure, AWS, GCP, Workspace)", group: "Configure", icon: "☁️" },
  { id: "ai", label: "AI Mode & Action Engine", group: "Configure", icon: "⚡" },
  { id: "ref-m365", label: "Microsoft 365 / Entra", group: "Reference", icon: "☁️" },
  { id: "ref-network", label: "Network devices", group: "Reference", icon: "🌐" },
  { id: "ref-portal", label: "Customer portal", group: "Reference", icon: "🏠" },
  { id: "ref-audit", label: "Audit log", group: "Reference", icon: "🔍" },
  { id: "ref-users", label: "Users & roles", group: "Reference", icon: "👥" },
  { id: "ai-correlation", label: "AI alert correlation", group: "Reference", icon: "🔗" },
  { id: "ai-patch-risk", label: "AI patch risk scoring", group: "Reference", icon: "⚠️" },
  {
    // Rendered from the APPLIANCE'S OWN registry rather than written out
    // here: the same /integrations/reference endpoint the printed PDF renders
    // from. Documenting 24 integrations by hand in two places guarantees they
    // disagree within a release.
    id: "integrations-reference",
    label: "Every integration & what it does",
    group: "Reference",
    icon: "🧩",
  },
  { id: "ai-auto-close", label: "AI ticket auto-close", group: "Reference", icon: "✅" },
  { id: "integrations", label: "Security, backup & DNS integrations", group: "Configure", icon: "🔌" },
  { id: "email-intake", label: "Email-to-ticket", group: "Configure", icon: "📥" },
  { id: "m365-tenants", label: "Multi-tenant M365", group: "Configure", icon: "☁️" },
  { id: "capacity-forecast", label: "Capacity forecasting", group: "Reference", icon: "📈" },
  { id: "meshcentral", label: "MeshCentral remote desktop", group: "Configure", icon: "🖥" },
  { id: "ha-shell-broker", label: "HA shell broker (Redis)", group: "Reference", icon: "🔁" },
  { id: "ai-compliance", label: "Compliance assessments", group: "Reference", icon: "📋" },
  { id: "meraki-licenses", label: "Network vendor licence expiry alerts", group: "Reference", icon: "📡" },
  { id: "demo-mode", label: "Demo mode (sales / training)", group: "Reference", icon: "🎬" },
  {
    // Split out of "mobile-pwa", which two sections shared - the
    // sidebar link resolved to whichever came first.
    id: "mobile-dashboard-push",
    label: "Mobile dashboard & push",
    group: "Reference",
    icon: "📲",
  },
  { id: "psa-module", label: "PSA — contracts, invoices, pipeline", group: "Reference", icon: "💼" },
  { id: "intune-devices", label: "Intune device admin", group: "Reference", icon: "📱" },
  { id: "ai-terminal", label: "AI Terminal (in-app shell)", group: "Reference", icon: "💬" },
  { id: "network-integrations", label: "Network vendor API integrations", group: "Configure", icon: "🔌" },
  { id: "deployment-onprem-offprem", label: "On-prem vs off-prem server", group: "Configure", icon: "🌐" },
  { id: "network-discovery", label: "Network discovery & auto-onboarding", group: "Reference", icon: "🔍" },
  { id: "custom-dashboards", label: "Custom dashboards", group: "Reference", icon: "📊" },
  { id: "mobile-pwa", label: "Install as a mobile app (PWA)", group: "How do I…", icon: "📱" },

  { id: "roles-access", label: "Roles & access (per-role features)", group: "Configure", icon: "🚦" },
  { id: "wf-software-coverage", label: "Find machines missing an app", group: "How do I…", icon: "🧭" },
  { id: "wf-portal-documents", label: "Give a client their own reports", group: "How do I…", icon: "📂" },
  { id: "wf-unlock-ad", label: "Unlock a locked-out account", group: "How do I…", icon: "🔓" },
  { id: "wf-offboard", label: "Offboard a leaver", group: "How do I…", icon: "👋" },
  { id: "passwords", label: "Password policy", group: "Configure", icon: "🔑" },
  { id: "scheduled-reports", label: "Scheduled reports", group: "Configure", icon: "⏰" },
  { id: "sessions", label: "Sessions & idle sign-out", group: "Reference", icon: "⏳" },
  { id: "ai-spend", label: "AI spend limits", group: "Configure", icon: "💷" },
  { id: "alert-digest", label: "Alert noise & the AI digest", group: "Configure", icon: "🔕" },
  { id: "ticket-tools", label: "Ticket tools: merge, notes, attachments, replies", group: "Reference", icon: "🧰" },
  { id: "portal-activity", label: "Portal activity log", group: "Reference", icon: "📜" },

  { id: "troubleshooting", label: "Troubleshooting", group: "Help", icon: "🛠️" },
  { id: "shortcuts", label: "Tips & shortcuts", group: "Help", icon: "✨" },
];

/**
 * The integrations reference, read live from the appliance.
 *
 * The vendor and capability lists come from the running code (see
 * services/integration_catalog.py), so this cannot describe an integration
 * that was removed or miss one that was added - and the downloadable PDF
 * renders from the identical source, so page and document cannot drift.
 */
function IntegrationReference() {
  type Cap = { name: string; detail: string; writes: boolean };
  type Entry = {
    key: string;
    name: string;
    family: string;
    what_it_is: string;
    setup: string[];
    capabilities: Cap[];
    reachability: string;
    limits: string[];
    verified: boolean;
    where_in_ui: string;
    can_write: boolean;
  };
  type Payload = {
    summary: {
      integrations: number;
      families: number;
      capabilities: number;
      write_capable: number;
      read_only: number;
      unverified: number;
    };
    integrations: Entry[];
  };

  const [data, setData] = useState<Payload | null>(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api
      .get("/api/v1/integrations/reference")
      .then((r) => setData(r.data))
      .catch(() => setErr(true));
  }, []);

  if (err)
    return (
      <p className="text-sm text-slate-500 dark:text-slate-400">
        Could not load the integration list from the appliance.
      </p>
    );
  if (!data) return <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>;

  const families: string[] = [];
  data.integrations.forEach((i) => {
    if (!families.includes(i.family)) families.push(i.family);
  });

  const capBadge = (writes: boolean) =>
    writes
      ? "bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300"
      : "bg-slate-100 text-slate-500 dark:bg-slate-700/50 dark:text-slate-400";

  return (
    <div className="space-y-6">
      <p>
        <strong>{data.summary.integrations} integrations</strong> across{" "}
        {data.summary.families} families, exposing{" "}
        <strong>{data.summary.capabilities} capabilities</strong>.{" "}
        {data.summary.write_capable} can make changes; {data.summary.read_only} can only read.
        Every capability below is tagged <strong>read</strong> or <strong>write</strong> —
        read means {PRODUCT_NAME} only looks, write means it can change something in the
        other system.
      </p>
      {data.summary.unverified > 0 && (
        <p className="text-sm text-amber-700 dark:text-amber-300">
          {data.summary.unverified} of them are network-controller adapters written from
          vendor documentation and <strong>not verified against live hardware</strong>. Their
          write actions stay switched off until an admin enables them after testing on their
          own equipment. They are listed so the gap is visible.
        </p>
      )}
      <p className="text-sm">
        {/* A plain href would navigate, carry no Authorization header, and
            show {"detail":"Not authenticated"} instead of the PDF. */}
        <button
          onClick={() =>
            downloadAuthed(
              "/api/v1/integrations/reference.pdf",
              "AeroGauge-integrations.pdf",
              "application/pdf",
            )
          }
          className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
        >
          Download this as a PDF
        </button>{" "}
        — same content, generated from the same registry.
      </p>

      {families.map((fam) => (
        <div key={fam}>
          <H3>{fam}</H3>
          <div className="space-y-4">
            {data.integrations
              .filter((i) => i.family === fam)
              .map((i) => (
                <div
                  key={i.key}
                  className="rounded-lg border border-slate-200 dark:border-slate-700 p-3"
                >
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="font-semibold text-slate-900 dark:text-slate-100">
                      {i.name}
                    </span>
                    <span className={`text-[11px] px-1.5 py-0.5 rounded ${capBadge(i.can_write)}`}>
                      {i.can_write ? "read + write" : "read only"}
                    </span>
                    {!i.verified && (
                      <span className="text-[11px] text-amber-700 dark:text-amber-400">
                        not verified against live hardware
                      </span>
                    )}
                  </div>
                  <p className="text-sm mt-1">{i.what_it_is}</p>
                  {i.where_in_ui && (
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      Where: {i.where_in_ui}
                    </p>
                  )}
                  {i.capabilities.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {i.capabilities.map((c) => (
                        <li key={c.name} className="text-sm flex gap-2">
                          <span
                            className={`shrink-0 text-[10px] mt-0.5 px-1 py-0.5 rounded font-mono ${capBadge(
                              c.writes,
                            )}`}
                          >
                            {c.writes ? "write" : "read"}
                          </span>
                          <span>
                            <strong>{c.name}</strong> — {c.detail}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {i.setup.length > 0 && (
                    <>
                      <div className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400 mt-3">
                        Setting it up
                      </div>
                      <ul className="list-disc pl-5 text-sm space-y-0.5">
                        {i.setup.map((s, n) => (
                          <li key={n}>{s}</li>
                        ))}
                      </ul>
                    </>
                  )}
                  {i.reachability && (
                    <>
                      <div className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400 mt-3">
                        How it connects
                      </div>
                      <p className="text-sm">{i.reachability}</p>
                    </>
                  )}
                  {i.limits.length > 0 && (
                    <>
                      <div className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400 mt-3">
                        Limits and gotchas
                      </div>
                      <ul className="list-disc pl-5 text-sm space-y-0.5">
                        {i.limits.map((s, n) => (
                          <li key={n}>{s}</li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function HSection({
  id,
  title,
  intro,
  group,
  icon,
  children,
}: {
  id: string;
  title: string;
  intro?: React.ReactNode;
  group: Group;
  icon?: string;
  children: React.ReactNode;
}) {
  const g = GROUP_STYLE[group];
  return (
    <section id={id} className="scroll-mt-24 mb-12 group">
      <div className="flex items-start gap-3 mb-3">
        <div className={`w-1 self-stretch rounded-sm ${g.accent}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {icon && <span className="text-xl">{icon}</span>}
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-slate-100">
              {title}
            </h2>
            <span
              className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${g.chip} ${g.chipText}`}
            >
              {group}
            </span>
          </div>
          {intro && <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">{intro}</p>}
        </div>
      </div>
      <div className="text-slate-700 dark:text-slate-300 leading-relaxed space-y-3 pl-4">{children}</div>
    </section>
  );
}

function H3({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-base font-bold text-slate-800 dark:text-slate-200 mt-5 mb-2 flex items-center gap-2">
      <span className="w-1 h-4 bg-slate-400 dark:bg-slate-500 rounded-sm" />
      {children}
    </h3>
  );
}

function Steps({ children }: { children: React.ReactNode }) {
  const items = React.Children.toArray(children);
  return (
    <ol className="space-y-2.5 my-3">
      {items.map((c, i) => (
        <li key={i} className="flex gap-3 items-start">
          <span className="shrink-0 w-7 h-7 rounded-full bg-slate-800 dark:bg-slate-600 text-white text-xs font-bold flex items-center justify-center">
            {i + 1}
          </span>
          <div className="flex-1 pt-0.5">{c}</div>
        </li>
      ))}
    </ol>
  );
}

function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <ul className="list-none space-y-1.5 my-2">
      {React.Children.map(children, (c, i) => (
        <li key={i} className="flex gap-2 items-start">
          <span className="text-slate-400 mt-1 shrink-0">▸</span>
          <div className="flex-1">{c}</div>
        </li>
      ))}
    </ul>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="px-2 py-0.5 text-xs font-mono bg-slate-100 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 dark:text-slate-100 rounded shadow-sm">
      {children}
    </kbd>
  );
}

function Path({ children }: { children: React.ReactNode }) {
  return (
    <span className="px-2 py-0.5 text-xs font-mono bg-blue-50 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 rounded font-semibold">
      {children}
    </span>
  );
}

function Tip({ children }: { children: React.ReactNode }) {
  return (
    <div className="my-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 border-l-4 border-l-amber-500 px-4 py-3 text-sm text-amber-900 dark:text-amber-200 flex gap-2">
      <span className="text-base shrink-0">💡</span>
      <div>
        <strong className="text-amber-800 dark:text-amber-300">Tip:</strong> {children}
      </div>
    </div>
  );
}

function Note({ children }: { children: React.ReactNode }) {
  return (
    <div className="my-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 border-l-4 border-l-slate-500 px-4 py-3 text-sm text-slate-700 dark:text-slate-300 flex gap-2">
      <span className="text-base shrink-0">📌</span>
      <div>{children}</div>
    </div>
  );
}

export default function HelpGuide() {
  // Logo + name from the single branding source of truth (has_logo is a real
  // server-side file check, logo_url is cache-busted). logoFailed guards
  // against rendering a broken-image icon if the file is somehow missing.
  const navigate = useNavigate();
  const branding = useBranding();
  const [logoFailed, setLogoFailed] = useState(false);
  // Help explains how THE PRODUCT works, so it names the product - not the
  // MSP whose install this is. "DCNS, LLC — Help & How-To" read as if DCNS
  // wrote the manual. The operator's own name still brands the console,
  // reports and invoices; see the per-surface rules in email_service.py.
  const companyName = branding.company_name || PRODUCT_NAME;
  const helpTitle = PRODUCT_NAME;
  const logoUrl = !logoFailed && branding.has_logo ? branding.logo_url : null;
  const [activeId, setActiveId] = useState<string>("overview");
  const [query, setQuery] = useState("");

  // Retitle the tab while Help is open, and PUT IT BACK on the way out.
  // Without the cleanup the title stuck on "Help & How-To" for the rest of the
  // session no matter where you navigated — harmless when this page opened in
  // its own tab (the tab closed), wrong now that it is a normal in-layout
  // route. Uses the deployment's own brand rather than a hardcoded product
  // name, so a white-labelled appliance titles its tab correctly.
  useEffect(() => {
    const previous = document.title;
    document.title = `Help & How-To — ${helpTitle}`;
    return () => {
      document.title = previous;
    };
  }, [helpTitle]);

  useEffect(() => {
    setLogoFailed(false);
  }, [branding.logo_url]);

  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (!el) return;
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) setActiveId(s.id);
          });
        },
        { rootMargin: "-30% 0px -60% 0px", threshold: 0 }
      );
      io.observe(el);
      observers.push(io);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, []);

  const q = query.trim().toLowerCase();
  const filtered = q ? SECTIONS.filter((s) => s.label.toLowerCase().includes(q)) : SECTIONS;
  const grouped: Record<string, Section[]> = {};
  filtered.forEach((s) => {
    if (!grouped[s.group]) grouped[s.group] = [];
    grouped[s.group].push(s);
  });

  // Group order
  const groupOrder: Group[] = ["Start here", "How do I…", "Configure", "Reference", "Help"];

  return (
    <div className="text-slate-900 dark:text-slate-100">
      {/* Top bar */}
      <header className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-sm mb-4">
        <div className="px-4 py-3 flex items-center gap-3">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={companyName}
              className="h-9 w-auto object-contain"
              onError={() => setLogoFailed(true)}
            />
          ) : (
            <div className="w-9 h-9 bg-slate-800 dark:bg-slate-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">
              {companyName.charAt(0)}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h1 className="text-lg md:text-xl font-bold leading-tight truncate text-slate-900 dark:text-slate-100">
              {helpTitle} — Help & How-To
            </h1>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 hidden sm:block">
              Step-by-step instructions · Auto-synced with feature changes
            </p>
          </div>
          <button
            onClick={() => window.print()}
            className="hidden sm:inline-flex text-xs px-3 py-1.5 rounded-md bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors font-medium"
          >
            Print
          </button>
          <button
            onClick={() => navigate(-1)}
            className="text-xs px-3 py-1.5 rounded-md bg-slate-800 dark:bg-slate-600 text-white hover:bg-slate-900 dark:hover:bg-slate-500 transition-colors font-medium"
          >
            ← Back
          </button>
        </div>
      </header>

      <div className="flex gap-6 relative">
        {/* Sidebar */}
        <aside className="hidden lg:block w-72 shrink-0">
          {/* Sticks to the top of <main>'s scrollport, not the window:
              main is the element that scrolls. 91 sections make keeping
              the index visible the whole point. */}
          <div className="sticky top-0">
            <div className="relative mb-4">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder='Filter (e.g. "ticket", "patch")…'
                className="w-full text-sm pl-9 pr-3 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 dark:text-slate-100 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder:text-slate-400 dark:placeholder:text-slate-500"
              />
              <svg
                className="absolute left-3 top-3 w-4 h-4 text-slate-400"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <nav className="space-y-5 max-h-[calc(100vh-12rem)] overflow-y-auto pr-1 -mr-1">
              {groupOrder.map((group) => {
                const items = grouped[group];
                if (!items || items.length === 0) return null;
                const g = GROUP_STYLE[group];
                return (
                  <div key={group}>
                    <div className="flex items-center gap-2 mb-2 px-2">
                      <span className={`w-2 h-2 rounded-full ${g.dot}`} />
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">
                        {group}
                      </div>
                    </div>
                    <div className="space-y-0.5">
                      {items.map((s) => (
                        <a
                          key={s.id}
                          href={`#${s.id}`}
                          className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-sm transition-colors ${
                            activeId === s.id
                              ? `${g.activeBg} text-white font-semibold`
                              : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/40"
                          }`}
                        >
                          {s.icon && <span className="text-sm">{s.icon}</span>}
                          <span className="truncate">{s.label}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 min-w-0">
          {/* Hero */}
          <div className="mb-8 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-900">
            <div className="p-8 md:p-10 text-white">
              <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-slate-800 border border-slate-700 text-xs font-medium mb-4">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
                Auto-synced with every feature change
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-2 tracking-tight">
                Learn AeroGauge in one window.
              </h2>
              <p className="text-slate-300 max-w-2xl text-sm md:text-base">
                Day-1 setup, common workflows, and a reference for every page. Filter the menu on
                the left or scroll. New to the app? Start at{" "}
                <a href="#day-one" className="underline decoration-slate-400 hover:decoration-white">
                  Day 1: First-time setup
                </a>
                .
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                {groupOrder.map((g) => {
                  const items = SECTIONS.filter((s) => s.group === g);
                  return (
                    <a
                      key={g}
                      href={`#${items[0]?.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-800 border border-slate-700 text-xs font-semibold hover:bg-slate-700 transition-colors"
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${GROUP_STYLE[g].dot}`} />
                      {g}
                      <span className="text-slate-500">·</span>
                      <span className="text-slate-300">{items.length}</span>
                    </a>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Content card */}
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 p-6 md:p-10">
            <HSection id="overview" title="Welcome" group="Start here" icon="🚀">
              <p>
                <strong>{helpTitle}</strong> is your shop's command center. From one web app
                you'll watch every customer's endpoints, take tickets, push patches, run scripts,
                and send customer-facing reports. This guide teaches you how to <em>use</em> it —
                start with the next two sections if you're brand new, then jump to whichever{" "}
                <em>"How do I…"</em> you need.
              </p>
              <Tip>
                The left-side menu has a filter box — type "ticket" or "alert" to jump straight to
                the workflow you want. Or use <Kbd>Ctrl</Kbd>+<Kbd>F</Kbd> to search this whole
                page.
              </Tip>
            </HSection>

            <HSection
              id="tour"
              title="Quick tour"
              intro="What's on the screen."
              group="Start here"
              icon="🗺️"
            >
              <p>The app has three persistent areas:</p>
              <Bullet>
                <li>
                  <strong>Left sidebar</strong> — main navigation. Dashboard at top, feature pages
                  below (Customers, Devices, Tickets, Calendar, Self-Healing, Patch Compliance,
                  Scripts, Software, Network, Reports). Admin-only pages (Microsoft 365, Users,
                  Audit Log, Settings) appear here only if you're an admin.
                </li>
                <li>
                  <strong>Sidebar footer</strong> — your account info, plus quick links: Security,{" "}
                  <strong>Help</strong> (this guide), Change password, Sign out, light/dark mode.
                </li>
                <li>
                  <strong>Main panel</strong> — whatever page you clicked. Tables show toolbar +
                  filters at top, detail pages use tabs.
                </li>
              </Bullet>
              <p>
                Click the logo or "Dashboard" to come back to home. URLs are bookmarkable — copy a
                device or ticket URL and paste it to a colleague.
              </p>
            </HSection>

            <HSection
              id="day-one"
              title="Day 1: First-time setup"
              intro="Do these once, in order, before you onboard your first customer."
              group="Start here"
              icon="🌅"
            >
              <Steps>
                <li>
                  <strong>Log in.</strong> Use the credentials you were given. If this appliance
                  was seeded with a default admin account rather than created through the setup
                  wizard, you'll be forced to change the password on first login — do it.
                </li>
                <li>
                  <strong>Set up MFA</strong> on your account. Open the sidebar footer →{" "}
                  <Path>Security</Path>. Click <strong>Enable MFA</strong>, scan the QR with
                  Authenticator/1Password/Authy, enter the 6-digit code to confirm.{" "}
                  <strong>Save the 8 backup codes</strong> shown right after — they're displayed
                  once and never shown again. See{" "}
                  <a href="#wf-2fa" className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium">
                    "Set up MFA"
                  </a>
                  .
                </li>
                <li>
                  <strong>Set company info.</strong> Sidebar → <Path>Settings</Path> (admin only).
                  Set Company Name, upload your Logo, set the Alert Email (where internal alerts
                  go), and the "device offline after N minutes" threshold.
                </li>
                <li>
                  <strong>Review default alert thresholds.</strong> Same Settings page →{" "}
                  <strong>Alert Thresholds</strong> card. Defaults are sensible (disk 80/85/95,
                  memory 80/85/90, etc.) but read them once so you know what counts as "warning"
                  vs "critical." See{" "}
                  <a href="#thresholds" className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium">
                    "Tuning alert thresholds"
                  </a>
                  .
                </li>
                <li>
                  <strong>Pick a patch policy.</strong> Settings → <strong>Patch Policy</strong>.
                  Default is "auto-approve security updates, manual for everything else." Change
                  if your shop's policy differs. See{" "}
                  <a href="#patch-policy" className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium">
                    "Choosing a patch policy"
                  </a>
                  .
                </li>
                <li>
                  <strong>Set the SLA policy.</strong> <Path>HelpDesk → Settings</Path> →{" "}
                  <strong>SLA policy (global)</strong>. Set response/resolution minutes for each
                  priority. Customers can override later.
                </li>
                <li>
                  <strong>Add staff accounts.</strong> Sidebar → <Path>Users</Path> (admin only).
                  Click "+ New user", set role (admin or tech), force-change-password on first
                  login.
                </li>
                <li>
                  <strong>Add your first customer.</strong> See{" "}
                  <a
                    href="#wf-add-customer"
                    className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
                  >
                    "Add a new customer"
                  </a>{" "}
                  below.
                </li>
              </Steps>
              <Tip>
                You don't need to configure webhooks, push notifications, or API tokens to start —
                add them later when a real need comes up.
              </Tip>
            </HSection>

            <HSection
              id="wf-add-customer"
              title="How do I add a new customer?"
              intro="Customers are the businesses you support. One row per tenant."
              group="How do I…"
              icon="🏢"
            >
              <Steps>
                <li>
                  Sidebar → <Path>Customers</Path>.
                </li>
                <li>
                  Click <strong>+ New customer</strong> (top right).
                </li>
                <li>
                  Fill in: <strong>Name</strong>, <strong>Contact email</strong> (this is where all
                  their customer-facing emails go — billing, QBR, SLA summaries, compliance
                  evidence), Phone, Address, Notes.
                </li>
                <li>
                  Click <strong>Save</strong>. You're dropped on the customer detail page.
                </li>
                <li>
                  Click the <strong>Groups</strong> tab → <strong>+ New group</strong>. Create at
                  least one group ("Workstations" is a good start). Groups let you target alert
                  threshold overrides and patch policies later.
                </li>
                <li>
                  Optional now (recommended later): <strong>Reports</strong> tab → set
                  per-customer <em>SLA Policy</em>, <em>Patch Policy</em>,{" "}
                  <em>Maintenance Window</em> overrides if their needs differ from global
                  defaults.
                </li>
              </Steps>
              <p>Now deploy the agent — next workflow.</p>
            </HSection>

            <HSection
              id="wf-deploy-agent"
              title="How do I deploy the agent to a customer's machines?"
              intro="Two paths: per-machine MSI install, or mass-deploy via an existing agent."
              group="How do I…"
              icon="📦"
            >
              <H3>Per-machine MSI (good for &lt;20 endpoints)</H3>
              <Steps>
                <li>
                  Sidebar → <Path>Onboarding</Path>.
                </li>
                <li>
                  Tab <strong>Issue install package</strong>. Pick the customer, optionally
                  pre-assign a device group.
                </li>
                <li>
                  Click <strong>Generate package</strong>. Wait a moment.
                </li>
                <li>
                  Tab <strong>Past packages</strong>. Download the MSI link for the package you
                  just created.
                </li>
                <li>
                  Email/share the MSI with whoever will run it on the customer endpoints. Have
                  them run <code>AeroGauge-Agent.exe /S</code> as administrator (or
                  double-click).
                </li>
                <li>
                  Within ~1 minute of install, the endpoint appears in <Path>Devices</Path>. See{" "}
                  <a
                    href="#wf-watch-devices"
                    className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
                  >
                    "Watch devices come in"
                  </a>
                  .
                </li>
              </Steps>
              <H3>Mass-deploy (good for ≥20 endpoints, or via GPO / existing agent)</H3>
              <Steps>
                <li>
                  Sidebar → <Path>Onboarding</Path> → tab{" "}
                  <strong>Mass deploy via existing agent</strong>.
                </li>
                <li>
                  Pick the customer + group. Generate the bootstrap script. The script is meant
                  to run via your existing management tool (GPO, your previous RMM, Intune, etc.).
                </li>
                <li>
                  Hand the script to your deployment tool. Endpoints appear in <Path>Devices</Path>{" "}
                  as they execute it.
                </li>
              </Steps>
              <Note>
                Watch the preflight banner at the top of Onboarding — it warns if anything is
                misconfigured (logo too large, missing alert email, etc.) before you generate.
              </Note>
              <H3>macOS &amp; Linux endpoints</H3>
              <p>
                Macs and Linux servers use the cross-platform agent in{" "}
                <code>agent/unix/</code>. Copy that folder to the box and run, as
                root:
              </p>
              <Steps>
                <li>
                  <code>
                    sudo ./install.sh --server https://rmm.example.com --secret
                    &lt;AGENT_SECRET&gt; [--customer-id N]
                  </code>
                </li>
                <li>
                  It installs under <code>/opt/aerogauge</code>, registers a{" "}
                  <strong>systemd</strong> unit (Linux) or{" "}
                  <strong>launchd</strong> daemon (macOS) — both start on boot
                  and restart on crash — and the device appears in{" "}
                  <Path>Devices</Path> within ~1 minute, tagged with its OS.
                </li>
              </Steps>
              <Note>
                The Unix agent supports run-script, restart-service, reboot,
                shutdown and check-in/inventory. Windows-only actions (winget,
                Windows Update, AD, MeshAgent) aren't applicable and report a
                clear "not supported" if ever queued. Agent self-update on
                Unix goes through the OS package manager / MDM, not in-app
                rollout.
              </Note>
            </HSection>

            <HSection
              id="wf-watch-devices"
              title="How do I watch new devices come in?"
              group="How do I…"
              icon="👀"
            >
              <Steps>
                <li>
                  Sidebar → <Path>Devices</Path>.
                </li>
                <li>
                  Filter by customer (top toolbar). New devices appear with status{" "}
                  <strong>online</strong> once their first heartbeat lands.
                </li>
                <li>
                  Click a device to open detail. Tabs: <strong>Overview</strong> (hostname, OS,
                  IP, identity badge), <strong>Metrics</strong>, <strong>Inventory</strong>,{" "}
                  <strong>Patches</strong>, <strong>Alerts</strong>, <strong>History</strong>,{" "}
                  <strong>BitLocker</strong>, <strong>Warranty</strong>.
                </li>
                <li>
                  Wait ~1 hour for the first <em>inventory</em> cycle — software list, hardware
                  health, RAID/PSU status all populate then. Metrics (CPU/mem/disk) populate on
                  every heartbeat (60s).
                </li>
              </Steps>
              <Tip>
                If a device shows up but hostname is empty or OS is "unknown," the first inventory
                cycle hasn't run yet. Come back in an hour.
              </Tip>
            </HSection>

            <HSection
              id="wf-triage-alerts"
              title="How do I triage the morning alerts?"
              intro="My recommended 5-minute routine."
              group="How do I…"
              icon="☕"
            >
              <Steps>
                <li>
                  Sidebar → <Path>Dashboard</Path>. Look at <strong>Health Snapshot</strong>:
                  critical alerts on top, then warnings, offline devices, failed self-heals,
                  unreachable network devices.
                </li>
                <li>
                  Click each critical alert to open the device. Confirm the metric is still in
                  alert state (sometimes spikes self-resolve).
                </li>
                <li>
                  For real issues: open a ticket from the device detail (or update an existing
                  ticket). Assign to a tech.
                </li>
                <li>
                  For false positives (e.g. dev box that pegs CPU on purpose): mute or raise the
                  threshold for that <em>specific customer</em> on{" "}
                  <Path>Customer detail → Reports → Alert Thresholds</Path>.
                </li>
                <li>
                  Look at <strong>SLA at risk</strong> (right panel) — any ticket trending to
                  breach gets immediate attention.
                </li>
                <li>
                  Check <strong>Failed self-heals (24h)</strong>. A service that won't restart on
                  its own usually means a bigger problem — go to the device and investigate.
                </li>
              </Steps>
            </HSection>

            <HSection
              id="wf-create-ticket"
              title="How do I open and work a ticket?"
              group="How do I…"
              icon="🎟️"
            >
              <Steps>
                <li>
                  Sidebar → <Path>Tickets</Path> → <strong>+ New ticket</strong>.
                </li>
                <li>
                  Pick the <strong>customer</strong>, set <strong>priority</strong> (this drives
                  the SLA timer — see SLA policy), write a clear subject + description.
                </li>
                <li>
                  Optionally link a <strong>device</strong> so the ticket shows up on that
                  device's detail page and inherits context.
                </li>
                <li>
                  Click <strong>Create</strong>. Status starts as <code>open</code>; clock is
                  running on response SLA.
                </li>
                <li>
                  Open the ticket. Add the first comment → status flips to <code>in_progress</code>{" "}
                  — response SLA stops, resolution SLA starts.
                </li>
                <li>
                  Update as you work (time entries, comments). Use <code>on_hold</code> when
                  waiting on the customer — both SLA clocks pause.
                </li>
                <li>
                  When fixed, set <code>resolved</code> → customer confirms → <code>closed</code>.
                </li>
              </Steps>
              <Note>
                SLA pause behavior is automatic — <code>on_hold</code>, <code>resolved</code>,{" "}
                <code>closed</code> all pause the clock. No manual SLA toggling required.
              </Note>
            </HSection>

            <HSection
              id="wf-sla-breach"
              title="How do I handle an SLA breach?"
              group="How do I…"
              icon="⏱️"
            >
              <Steps>
                <li>
                  The Dashboard's <strong>SLA at risk</strong> panel sorts tickets worst-consumed
                  first. Tickets in red are already breached; amber are within 25% of breach.
                </li>
                <li>Click the breached ticket — open it.</li>
                <li>
                  Re-prioritize honestly: if it's truly higher than the customer initially
                  reported, bump priority (this also changes the remaining SLA window).
                </li>
                <li>
                  If the breach was caused by waiting on the customer, double-check the ticket
                  spent time in <code>on_hold</code> — that pauses the clock. If it didn't, that's
                  the gap to plug in your team's process.
                </li>
                <li>
                  On Monday, the weekly SLA Breach Summary email goes out to each customer who
                  had breaches in the last 7 days — review the preview first (see{" "}
                  <a
                    href="#wf-sla-summary"
                    className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
                  >
                    workflow
                  </a>
                  ) to make sure the message is accurate.
                </li>
              </Steps>
            </HSection>

            <HSection
              id="wf-run-script"
              title="How do I run a script on a device?"
              group="How do I…"
              icon="📜"
            >
              <Steps>
                <li>
                  Sidebar → <Path>Scripts</Path>. The library is your shop's vetted PowerShell.
                </li>
                <li>
                  Click a script to see its description, expected parameters, and recent execution
                  log.
                </li>
                <li>
                  Click <strong>Run</strong>. Select one or more target devices. Fill any
                  parameters.
                </li>
                <li>
                  Click <strong>Execute</strong>. The agent picks up the command on its next poll
                  (within a minute), runs it, and streams output back.
                </li>
                <li>
                  Result appears under the script's execution log. Failures also surface in the
                  Alerts feed for that device.
                </li>
              </Steps>
              <Tip>
                Running scripts is always audited. Every execution writes a row in the Audit Log
                with who, what, where, when.
              </Tip>
            </HSection>

            <HSection
              id="wf-remote-shell"
              title="How do I open a remote shell on a device?"
              group="How do I…"
              icon="🖥️"
            >
              <Steps>
                <li>
                  Sidebar → <Path>Devices</Path> → click the device.
                </li>
                <li>
                  Top-right corner of the device detail page: <strong>Remote shell</strong>{" "}
                  button. (Hidden for non-admin roles.)
                </li>
                <li>
                  A live PowerShell terminal opens (xterm.js). Type as you would in a normal
                  shell. Commands run on the endpoint as the agent's user.
                </li>
                <li>Close the tab when done. The session is recorded in the Audit Log.</li>
              </Steps>
            </HSection>

            <HSection
              id="wf-approve-patches"
              title="How do I approve patches?"
              group="How do I…"
              icon="📦"
            >
              <p>
                Whether you need to approve at all depends on your{" "}
                <a href="#patch-policy" className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium">
                  patch policy
                </a>
                . On "auto-approve" you don't — patches flow through. On "manual":
              </p>
              <Steps>
                <li>
                  Sidebar → <Path>Reports &amp; compliance → Patch compliance</Path>.
                </li>
                <li>
                  Filter to <strong>Pending approval</strong>. You see one row per customer-patch
                  combination.
                </li>
                <li>Inspect each: KB number, severity, affected device count, classification.</li>
                <li>
                  Click <strong>Approve</strong> (or <strong>Reject</strong>). Approved patches
                  queue for the next maintenance-window pass; the dispatcher runs every 5 minutes
                  and respects each customer's maintenance window.
                </li>
                <li>
                  Track install progress on the <strong>Approved → installing</strong> column.
                  Failures appear with an error code; click to see the agent's full error.
                </li>
              </Steps>
            </HSection>

            <HSection
              id="wf-reset-ad"
              title="How do I reset an Active Directory password?"
              group="How do I…"
              icon="🔑"
            >
              <Steps>
                <li>
                  Sidebar → <Path>Active Directory</Path>.
                </li>
                <li>Filter by customer, search by username.</li>
                <li>
                  Click the user row → <strong>Reset password</strong>.
                </li>
                <li>
                  Enter the new password (or click "Generate"). Optionally check "Force change on
                  next login."
                </li>
                <li>
                  Click <strong>Reset</strong>. The action queues on the customer's agent (their
                  Domain Controller). Within ~1 minute the action completes and a result lands in
                  the Audit Log.
                </li>
                <li>
                  For locked accounts, use the <strong>Unlock</strong> button instead (no
                  password change needed).
                </li>
              </Steps>
              <Note>
                That page manages <strong>on-prem AD</strong> accounts via the
                customer's Domain Controller. For <strong>cloud (Entra)</strong>{" "}
                accounts, see the next section.
              </Note>
            </HSection>

            <HSection
              id="wf-entra-users"
              title="How do I manage a cloud (Entra) user?"
              group="How do I…"
              icon="☁️"
            >
              <p>
                Cloud-only identities don't live on a Domain Controller, so
                their actions go through Microsoft Graph using the customer's
                M365 app registration (set under{" "}
                <Path>Microsoft 365 → Tenants</Path>).
              </p>
              <Steps>
                <li>
                  Sidebar → <Path>Microsoft 365 → Entra Users</Path>. Pick the
                  customer.
                </li>
                <li>
                  Each row shows a <strong>Source</strong> badge —{" "}
                  <strong>Cloud</strong> (Entra-only) or <strong>Hybrid</strong>{" "}
                  (synced from on-prem AD) — plus MFA and enabled status.
                </li>
                <li>
                  Use <strong>Disable / Re-enable</strong>,{" "}
                  <strong>Reset Password</strong> (generated password shown
                  once), or <strong>Revoke Sessions</strong> (force re-sign-in
                  everywhere). Graph applies these immediately.
                </li>
              </Steps>
              <Note>
                For <strong>Hybrid</strong> users, on-prem AD is authoritative:
                disabling or resetting in the cloud gets overwritten on the next
                directory sync. The app steers you to AD Users for those, but
                offers a "force cloud action anyway" override if you understand
                the trade-off. Revoke-sessions and re-enable are always safe in
                the cloud.
              </Note>
            </HSection>

            <HSection
              id="wf-store-creds"
              title="How do I store a customer credential safely?"
              group="How do I…"
              icon="🔐"
            >
              <Steps>
                <li>
                  Sidebar → <Path>Customers</Path> → click the customer →{" "}
                  <strong>Credentials</strong> tab.
                </li>
                <li>
                  Click <strong>+ New credential</strong>.
                </li>
                <li>
                  Fill: <strong>Name</strong> ("Domain admin", "Firewall web UI"), Username,{" "}
                  <strong>Secret</strong> (paste the password, or click the generator for a
                  20-char random one), Notes.
                </li>
                <li>
                  Save. Secret is AES-256-GCM encrypted with a master key separate from BitLocker
                  escrow. The plaintext is not stored anywhere readable.
                </li>
                <li>
                  To use it later: click the row → <strong>Reveal</strong>. A modal pops with a
                  show/hide toggle and copy-to-clipboard.{" "}
                  <strong>Every reveal is audit-logged</strong> with your username + timestamp —
                  and the credential record itself shows "last accessed by &lt;you&gt; at &lt;time&gt;."
                </li>
              </Steps>
              <Note>
                Edit doesn't require re-entering the secret — leaving the secret field blank keeps
                the existing one. Delete is admin-only.
              </Note>
            </HSection>

            <HSection
              id="wf-bulk-assign"
              title="How do I bulk-assign or unassign devices?"
              group="How do I…"
              icon="🗂️"
            >
              <Steps>
                <li>
                  Sidebar → <Path>Devices</Path>. Filter so the rows you want are visible.
                </li>
                <li>
                  Check the boxes next to each device, or use the{" "}
                  <strong>Select all in filter</strong> box at the top.
                </li>
                <li>
                  The action bar at the bottom shows up. Click{" "}
                  <strong>Assign to customer…</strong> (or <strong>Unassign customer</strong>).
                </li>
                <li>
                  Pick the target customer + optional group. Confirm. Up to 500 devices per
                  request. One audit log row per batch.
                </li>
              </Steps>
            </HSection>

            <HSection
              id="wf-build-report"
              title="How do I build a custom report?"
              group="How do I…"
              icon="📊"
            >
              <Steps>
                <li>
                  Sidebar → <Path>Reports &amp; compliance → Reports</Path> → <strong>+ New report</strong>.
                </li>
                <li>
                  Pick a report type: <strong>Devices</strong>, <strong>Tickets</strong>, or{" "}
                  <strong>Alerts</strong>.
                </li>
                <li>
                  <strong>Columns</strong>: check the columns you want in the output. Headers will
                  show human-readable labels, not the internal column names.
                </li>
                <li>
                  <strong>Filters</strong>: add any number. Each filter knows the column's type —
                  strings get contains/in/eq, numbers get relational ops, datetimes get "last N
                  days" plus before/after.
                </li>
                <li>
                  <strong>Order by</strong> + <strong>row limit</strong> (max 5,000).
                </li>
                <li>
                  Click <strong>Preview</strong>. Tweak. When happy, <strong>Save</strong>.
                </li>
                <li>
                  On the saved report's page: <strong>Run</strong> any time,{" "}
                  <strong>Download CSV/PDF</strong>, or <strong>+ Schedule</strong>.
                </li>
                <li>
                  Schedules accept cadence (daily/weekly/monthly), time, day-of-week /
                  day-of-month, output format, and recipient list. One email per recipient (no
                  BCC).
                </li>
              </Steps>
            </HSection>

            <HSection
              id="wf-send-qbr"
              title="How do I send a Quarterly Business Review (QBR) to a customer?"
              group="How do I…"
              icon="📈"
            >
              <Steps>
                <li>
                  Sidebar → <Path>Customers</Path> → click the customer →{" "}
                  <strong>Reports</strong> tab.
                </li>
                <li>
                  Top of tab: <strong>Quarterly Business Review</strong> card. Pick a quarter from
                  the dropdown (default is the most-recently-closed quarter — never current).
                </li>
                <li>
                  Click <strong>Preview</strong> to open the PDF in a new tab; verify content. Or{" "}
                  <strong>Download</strong> to save it locally.
                </li>
                <li>
                  When ready: <strong>Email to customer</strong> (admin only). Confirm modal lets
                  you override the recipient address if the customer's primary contact_email isn't
                  where it should go. Click Send.
                </li>
                <li>
                  The audit log gets a <code>qbr_email</code> row with recipient + quarter.
                </li>
              </Steps>
            </HSection>

            <HSection
              id="wf-compliance"
              title="How do I generate a Compliance Evidence package?"
              group="How do I…"
              icon="📄"
            >
              <Steps>
                <li>
                  Customer detail → <strong>Reports</strong> tab →{" "}
                  <strong>Compliance Evidence</strong> card.
                </li>
                <li>
                  Pick the period: <code>YYYY-QN</code> (e.g. <code>2026-Q1</code>) or{" "}
                  <code>NNd</code> (e.g. <code>90d</code> for last 90 days).
                </li>
                <li>
                  <strong>Preview</strong> renders the executive PDF (1–3 pages: users + access
                  review, audit slice, patch compliance, alert MTTR, SLA performance, self-healing,
                  encryption coverage, backup coverage, controls in force).
                </li>
                <li>
                  <strong>Download ZIP</strong> (admin only) gives you the PDF +{" "}
                  <code>MANIFEST.txt</code> + CSVs (users, audit_log, alerts, alert_thresholds)
                  for the customer's auditor.
                </li>
                <li>
                  <strong>Email to customer</strong> sends the ZIP from the no-reply mailbox with
                  <code> Reply-To: support@</code>. Audit logged.
                </li>
              </Steps>
              <Note>
                That's the evidence package. For a <strong>framework-specific</strong>{" "}
                control report (HIPAA / SOX / GLBA / SOC 2 / PCI / NIST CSF / CIS)
                and to capture the org's sign-off, see <Path>Reports &amp; compliance → Compliance</Path>{" "}
                and the next section.
              </Note>
            </HSection>

            <HSection
              id="wf-attest"
              title="How do I record an organization's compliance attestation?"
              group="How do I…"
              icon="✍️"
            >
              <p>
                Framework reports and evidence collectors are the MSP's side of
                the story. An <strong>attestation</strong> is the org's side —
                the customer's responsible officer formally accepting the
                posture. You record it on their behalf and it then appears on
                the framework report PDF.
              </p>
              <Steps>
                <li>
                  Sidebar → <Path>Reports &amp; compliance → Compliance</Path>. Pick the{" "}
                  <strong>framework</strong> and <strong>customer scope</strong>{" "}
                  (or MSP-wide).
                </li>
                <li>
                  In the <strong>Organization attestation</strong> card, enter
                  the signatory's name (required), title, email, an optional
                  statement, and a period like <code>2026 Q2</code>.
                </li>
                <li>
                  Click <strong>Record attestation</strong>. The
                  current readiness % is snapshotted, so the report flags drift
                  if the posture later changes.
                </li>
                <li>
                  The framework report PDF now shows an{" "}
                  <strong>Organization attestation</strong> section. Re-attesting
                  supersedes the prior one; <strong>Revoke</strong> clears it.
                </li>
              </Steps>
              <Note>
                Every attestation, supersession, and revocation is written to the
                audit log (<code>compliance_attest</code> /{" "}
                <code>compliance_attest_revoke</code>) so you can prove who signed
                off and when.
              </Note>
            </HSection>

            <HSection
              id="wf-sla-summary"
              title="How do I send a customer their weekly SLA Breach Summary?"
              group="How do I…"
              icon="📧"
            >
              <p>
                It sends automatically every Monday at 09:00 ET — but you can preview or
                force-send manually:
              </p>
              <Steps>
                <li>
                  Customer detail → <strong>SLA Policy</strong> editor → below it,{" "}
                  <strong>SLA Breach Summary</strong> card.
                </li>
                <li>
                  Set the day-range (default 7 days). Click <strong>Preview</strong> to render the
                  HTML in a new tab.
                </li>
                <li>
                  <strong>Send to customer</strong> (admin only) confirms the recipient + day
                  range. If there are zero breaches in the period, the send is skipped unless you
                  check <strong>Force</strong> (rare — we don't want to train customers to ignore
                  the "From:" address).
                </li>
              </Steps>
            </HSection>

            <HSection
              id="wf-runbook"
              title="How do I write and share a runbook with a customer?"
              group="How do I…"
              icon="📘"
            >
              <Steps>
                <li>
                  Customer detail → <strong>Runbooks</strong> tab → <strong>+ New runbook</strong>.
                </li>
                <li>
                  Title + description. Set <strong>Visibility</strong>:
                  <Bullet>
                    <li>
                      <strong>Internal</strong> — staff only.
                    </li>
                    <li>
                      <strong>Customer</strong> — also visible on the customer portal.
                    </li>
                  </Bullet>
                </li>
                <li>
                  Set scope: <strong>This customer</strong> (default) or <strong>Global</strong>{" "}
                  (all customers).
                </li>
                <li>
                  Add steps. Each step has a title + body. Reorder with drag-handles, delete with
                  the X, add with <strong>+ Step</strong>.
                </li>
                <li>
                  Save. Customer portal users see "Customer"-visible runbooks scoped to their
                  tenant.
                </li>
              </Steps>
            </HSection>

            <HSection
              id="wf-onboarding-portal"
              title="How do I onboard a customer portal user?"
              group="How do I…"
              icon="👤"
            >
              <Steps>
                <li>
                  Customer detail → <strong>Portal Users</strong> tab →{" "}
                  <strong>+ New portal user</strong>.
                </li>
                <li>
                  Enter the customer contact's name, email, and a temporary password (or generate
                  one).
                </li>
                <li>
                  Send the customer the portal URL (<code>/portal</code>) and their temp
                  credentials — they'll be forced to change the password on first login.
                </li>
                <li>
                  They can now sign in to see their tickets, devices, invoices, and customer-visible
                  runbooks. They cannot see other customers' data — tenant isolation is enforced
                  server-side.
                </li>
              </Steps>
            </HSection>

            <HSection
              id="wf-rollout-agent"
              title="How do I roll out a new agent version safely?"
              group="How do I…"
              icon="🚢"
            >
              <Steps>
                <li>
                  Sidebar → <Path>Settings</Path> → <strong>Agent Builds</strong> card.
                </li>
                <li>
                  <strong>Upload MSI</strong>. The server captures a SHA-256 hash at upload time —
                  agents will refuse to install on hash mismatch.
                </li>
                <li>
                  Set <strong>Target version</strong> = your new version string. Move the{" "}
                  <strong>Rollout percent</strong> slider to <code>10</code>.
                </li>
                <li>
                  Wait. The "first 10%" of agents (deterministic bucket — same 10% every time)
                  pick up the update on their next poll. Watch the <strong>Update status</strong>{" "}
                  column in Devices.
                </li>
                <li>
                  If failures appear (<code>last_update_error</code>), pause by setting rollout to{" "}
                  <code>0</code>. Investigate, fix, retry.
                </li>
                <li>
                  If healthy: bump rollout to <code>25</code>, then <code>50</code>, then{" "}
                  <code>100</code>. Devices already on the target version are skipped.
                </li>
              </Steps>
              <Note>
                First-time bootstrap: this only works if the currently-deployed agent understands
                the <code>update_agent</code> command. Older agents need a one-time manual MSI
                install. After that, every future version rolls out from this page.
              </Note>
            </HSection>

            <HSection
              id="wf-fix-autostart"
              title="How do I fix agents that don't come back after a reboot?"
              group="How do I…"
              icon="🔁"
            >
              <p>
                Agents installed before mid-2026 may have the{" "}
                <code>AeroGaugeRMMAgent</code> Windows service set to{" "}
                <strong>Manual</strong> start, so the agent goes quiet after a
                reboot until someone logs in. New installs ship as{" "}
                <strong>Automatic</strong> via the installer — this fixes the
                ones already in the field.
              </p>
              <Steps>
                <li>
                  Sidebar → <Path>Devices</Path>. If any Windows devices still
                  need the fix, an amber banner appears at the top with a count.
                </li>
                <li>
                  Click <strong>Remediate all</strong> (admins only). This
                  queues a one-time <code>set_service_autostart</code> command
                  on every affected device.
                </li>
                <li>
                  Each device applies it on its next check-in (within ~1
                  minute) by running{" "}
                  <code>sc config AeroGaugeRMMAgent start= auto</code>, then reports
                  back. The banner's count drops as devices confirm.
                </li>
              </Steps>
              <Note>
                It's idempotent — safe to click repeatedly. Devices already
                fixed or with a fix in flight are skipped, so the banner
                disappears once the fleet is fully covered. You can also fire it
                for a single endpoint via the device's API if you're scripting.
              </Note>
            </HSection>

            <HSection
              id="wf-webhook"
              title="How do I wire Slack/Teams/PagerDuty for critical alerts?"
              group="How do I…"
              icon="🔔"
            >
              <Steps>
                <li>
                  Get the webhook URL from your tool (Slack incoming webhook, a Teams Workflow, or "Incoming
                  Webhook" connector, or PagerDuty Events API integration key).
                </li>
                <li>
                  Sidebar → <Path>Settings</Path> → <strong>Webhooks</strong> card →{" "}
                  <strong>+ New webhook</strong>.
                </li>
                <li>
                  Pick <strong>Kind</strong> (Slack / Teams / PagerDuty / Generic). Paste the URL.
                  For PagerDuty, paste the <strong>integration key</strong> instead.
                </li>
                <li>
                  <strong>Event types</strong> — check the events to subscribe. Typical setup:
                  <code> alert.critical</code> + <code>sla.response_breach</code> +{" "}
                  <code>sla.resolution_breach</code>.
                </li>
                <li>
                  Save. Click <strong>Test</strong> on the row — fires a synthetic event, shows
                  outcome inline.
                </li>
                <li>
                  Each row shows <em>Last status</em> with last success / failure / error so you
                  can spot a dead webhook quickly.
                </li>
              </Steps>
            </HSection>

            <HSection
              id="wf-2fa"
              title="How do I set up MFA on my own account?"
              group="How do I…"
              icon="🛡️"
            >
              <Steps>
                <li>
                  Sidebar footer → <Path>Security</Path>.
                </li>
                <li>
                  Click <strong>Enable MFA</strong>. A modal opens with a QR code.
                </li>
                <li>
                  Scan with Google Authenticator, 1Password, Authy, or any TOTP app. (No scanner?
                  Click "Show manual secret" and type it.)
                </li>
                <li>
                  Type the current 6-digit code from the app to confirm. Click{" "}
                  <strong>Verify</strong>.
                </li>
                <li>
                  <strong>Save the 8 backup codes shown next</strong>. Copy them to your password
                  manager. They're <em>only</em> shown once. Each is single-use; you can use them
                  if you lose your phone.
                </li>
                <li>Next login becomes 2-step: password → 6-digit code.</li>
              </Steps>
              <Tip>
                Lost your phone? On the login page click "Use a backup code instead" — enter one
                of the <code>ABCD-1234-WXYZ</code> codes. Then re-enroll MFA immediately and save
                fresh backup codes.
              </Tip>
            </HSection>

            <HSection
              id="wf-sso"
              title="How do I sign in with my company SSO (Entra / Okta / Google)?"
              group="How do I…"
              icon="🔑"
            >
              <p>
                Once an admin has configured your IdP in{" "}
                <Path>Settings → Single sign-on (OIDC)</Path>, a{" "}
                <strong>Sign in with {`{IdP name}`}</strong> button appears on the login page
                beneath the password fields.
              </p>
              <Steps>
                <li>Open the AeroGauge login page.</li>
                <li>
                  Click <strong>Sign in with {`{your IdP}`}</strong>. You'll be redirected to
                  the IdP, where you complete whatever sign-in flow your company uses (password,
                  MFA, biometrics, conditional access, etc.).
                </li>
                <li>
                  On success you bounce back into AeroGauge, already signed in. First time through,
                  a user account is created for you with the role the admin chose as the
                  default. Subsequent sign-ins reuse the same account.
                </li>
              </Steps>
              <Note>
                SSO and local password sign-in coexist — if you already have a local password,
                signing in with SSO links them so either path works. MFA enforcement still lives
                at the IdP for SSO sign-ins; AeroGauge's own TOTP only applies to local-password
                logins.
              </Note>
            </HSection>

            <HSection
              id="wf-connect-azure"
              title="How do I connect a customer's Azure subscription?"
              group="How do I…"
              icon="☁️"
            >
              <p>
                Connecting an Azure subscription lets AeroGauge (and the AI, via the action
                queue) list resources and run management actions like start/stop/restart VM.
                Credentials live encrypted at rest and are scoped to one customer.
              </p>
              <Steps>
                <li>
                  In the customer's Azure tenant, create a Service Principal scoped to the
                  subscription:
                  <code className="block bg-slate-900 text-emerald-300 p-3 my-2 text-xs rounded-lg font-mono shadow-inner">
                    az ad sp create-for-rbac --name "aerogauge-rmm" --role Contributor \\<br />
                    {"  "}--scopes /subscriptions/&lt;SUBSCRIPTION_ID&gt;
                  </code>
                  This emits four values: <code>tenant</code>, <code>appId</code>,{" "}
                  <code>password</code>, and you separately know the subscription ID.
                </li>
                <li>
                  In AeroGauge, open the customer's detail page → <strong>Cloud</strong> tab →{" "}
                  <strong>Add connector</strong>.
                </li>
                <li>
                  Pick platform <strong>Microsoft Azure</strong>, name it (e.g.{" "}
                  <code>Prod subscription</code>), and paste:
                  <Bullet>
                    <li>Tenant ID = <code>tenant</code> from the SP output</li>
                    <li>Client ID = <code>appId</code></li>
                    <li>Client secret = <code>password</code></li>
                    <li>Subscription ID = the subscription you scoped the SP to</li>
                  </Bullet>
                </li>
                <li>
                  Save. Click <strong>Test</strong> on the row — it lists a few resource groups
                  to verify auth works. A green "success" means you're good.
                </li>
              </Steps>
              <Note>
                For production we recommend a custom least-privilege role instead of{" "}
                <code>Contributor</code> — pick exactly the resource providers you want AeroGauge
                to touch (e.g. Compute + Resources + Storage read).
              </Note>
            </HSection>

            <HSection
              id="wf-connect-aws"
              title="How do I connect a customer's AWS account?"
              group="How do I…"
              icon="☁️"
            >
              <p>
                Connecting an AWS account lets AeroGauge list EC2 instances, EBS volumes, S3
                buckets, and VPCs, plus start/stop/reboot EC2 and snapshot EBS volumes through
                the AI Actions approval queue. Credentials are AES-encrypted at rest.
              </p>
              <Steps>
                <li>
                  In the customer's AWS account, create an IAM user — call it{" "}
                  <code>aerogauge-rmm</code>. Attach the AWS-managed{" "}
                  <code>PowerUserAccess</code> policy for v1, or a custom least-privilege
                  policy covering: <code>sts:GetCallerIdentity</code>,{" "}
                  <code>ec2:Describe*</code>, <code>ec2:StartInstances</code>,{" "}
                  <code>ec2:StopInstances</code>, <code>ec2:RebootInstances</code>,{" "}
                  <code>ec2:CreateSnapshot</code>, <code>s3:ListAllMyBuckets</code>,{" "}
                  <code>s3:GetBucketLocation</code>.
                </li>
                <li>
                  Generate an access key for that IAM user (Security credentials →{" "}
                  <strong>Create access key</strong>) and capture the <code>access_key_id</code>{" "}
                  and <code>secret_access_key</code>.
                </li>
                <li>
                  In AeroGauge, open the customer's detail page → <strong>Cloud</strong> tab →{" "}
                  <strong>Add connector</strong>.
                </li>
                <li>
                  Pick platform <strong>Amazon Web Services</strong>, name it (e.g.{" "}
                  <code>Prod account</code>), and paste:
                  <Bullet>
                    <li>Access key ID = the <code>AKIA…</code> string</li>
                    <li>Secret access key = the matching secret</li>
                    <li>Default region = the region you want to inventory first (e.g. <code>us-east-1</code>)</li>
                  </Bullet>
                </li>
                <li>
                  Save. Click <strong>Test</strong> on the row — it runs{" "}
                  <code>sts:GetCallerIdentity</code> and enumerates enabled regions. Green
                  "success" with the account ID returned means you're good.
                </li>
              </Steps>
              <H3>Cross-account / role assumption (optional)</H3>
              <p>
                For MSP setups where the customer wants to grant access to a role in their
                account (rather than handing over an IAM user), the connector accepts
                two extra fields:
              </p>
              <Bullet>
                <li>
                  <code>assume_role_arn</code> — ARN of the role to assume in the customer's
                  account (e.g. <code>arn:aws:iam::123456789012:role/aerogauge-rmm</code>). The role
                  must trust the IAM user you configured above.
                </li>
                <li>
                  <code>external_id</code> — optional shared secret the customer requires when
                  assuming the role; pass-through to <code>sts:AssumeRole</code>.
                </li>
              </Bullet>
              <Note>
                Cross-account is the recommended pattern for MSPs at scale — your access keys
                stay in your AWS account, customers can rotate the trust policy without you
                touching their IAM users, and CloudTrail in their account clearly shows the
                source identity.
              </Note>
              <H3>What AeroGauge can do once connected</H3>
              <Bullet>
                <li>Read: EC2 instances, EBS volumes, S3 buckets, VPCs (single-region for EC2/EBS/VPC; S3 is account-wide).</li>
                <li>
                  Write (always via AI Actions approval queue):{" "}
                  <code>aws_ec2_start</code>, <code>aws_ec2_stop</code>,{" "}
                  <code>aws_ec2_reboot</code> (HIGH risk),{" "}
                  <code>aws_ec2_snapshot</code>.
                </li>
              </Bullet>
            </HSection>

            <HSection
              id="wf-connect-gcp"
              title="How do I connect a customer's GCP project?"
              group="How do I…"
              icon="☁️"
            >
              <p>
                Connecting a GCP project lets AeroGauge list Compute Engine instances,
                persistent disks, Cloud Storage buckets, and VPC networks, plus
                start/stop/reset GCE instances and snapshot disks through the AI Actions
                approval queue. Credentials are AES-encrypted at rest.
              </p>
              <Steps>
                <li>
                  In the customer's GCP project, open <strong>IAM &amp; Admin → Service Accounts</strong>{" "}
                  and create a service account named <code>aerogauge-rmm</code>.
                </li>
                <li>
                  Grant the service account the roles you want. v1 starter set:{" "}
                  <code>roles/compute.instanceAdmin.v1</code> +{" "}
                  <code>roles/compute.viewer</code> +{" "}
                  <code>roles/storage.objectViewer</code>. Tighten later if you want a
                  least-privilege custom role.
                </li>
                <li>
                  On the service account, open <strong>Keys → Add key → Create new key (JSON)</strong>.
                  Download the JSON file — this is your single piece of credential material.
                </li>
                <li>
                  In AeroGauge, open the customer's detail page → <strong>Cloud</strong> tab →{" "}
                  <strong>Add connector</strong>.
                </li>
                <li>
                  Pick platform <strong>Google Cloud Platform</strong>, name it (e.g.{" "}
                  <code>Prod project</code>), set the <strong>Project ID</strong>, and paste
                  the entire JSON key contents into <strong>Service account JSON key</strong>.
                </li>
                <li>
                  Save. Click <strong>Test</strong> on the row — it parses the JSON, verifies the
                  project_id matches, and lists zones in the project. Green "success" with the
                  SA email shown means you're good.
                </li>
              </Steps>
              <Note>
                The service account JSON contains a private key. Treat it like a long-lived
                password — store the original safely and rotate it (Keys → delete) if AeroGauge
                no longer needs access to that customer.
              </Note>
              <H3>What AeroGauge can do once connected</H3>
              <Bullet>
                <li>Read: Compute Engine instances + disks (aggregated across all zones), Cloud Storage buckets, VPC networks.</li>
                <li>
                  Write (always via AI Actions approval queue):{" "}
                  <code>gcp_compute_instance_start</code>,{" "}
                  <code>gcp_compute_instance_stop</code>,{" "}
                  <code>gcp_compute_instance_reset</code> (HIGH risk — equivalent to a hard reset),{" "}
                  <code>gcp_compute_disk_snapshot</code>.
                </li>
              </Bullet>
            </HSection>

            <HSection
              id="wf-connect-gworkspace"
              title="How do I connect a customer's Google Workspace?"
              group="How do I…"
              icon="👥"
            >
              <p>
                Workspace is the identity layer (users, groups, OUs) — the Workspace
                connector lets AeroGauge list users + groups and (via the AI Actions
                approval queue) create users, suspend or unsuspend them, rotate passwords,
                and manage group membership. Auth uses a GCP service account with
                <strong> domain-wide delegation</strong>, impersonating a real Workspace
                super-admin.
              </p>
              <Steps>
                <li>
                  In any GCP project (yours or the customer's — Workspace ignores the
                  project; only the SA's identity matters), create a service account
                  named <code>aerogauge-rmm-workspace</code>.
                </li>
                <li>
                  Generate a JSON key for the SA and capture its <strong>client ID</strong>
                  {" "}(the numeric one shown next to the SA in the GCP console).
                </li>
                <li>
                  In the customer's Workspace admin (admin.google.com) → <strong>Security
                  → API controls → Manage Domain-wide Delegation → Add new</strong>.
                  Paste the SA client ID and grant these scopes:
                  <code className="block bg-slate-900 text-emerald-300 p-3 my-2 text-xs rounded-lg font-mono shadow-inner whitespace-pre-wrap">
                    https://www.googleapis.com/auth/admin.directory.user
                    {"\n"}
                    https://www.googleapis.com/auth/admin.directory.group
                  </code>
                </li>
                <li>
                  Pick a real Workspace super-admin user the SA should impersonate
                  (e.g. <code>admin@customer.com</code>). The SA only acts through this
                  user's identity — its actions appear in Workspace audit logs as that
                  admin.
                </li>
                <li>
                  In AeroGauge, open the customer's detail page → <strong>Cloud</strong> tab →{" "}
                  <strong>Add connector</strong>. Pick platform{" "}
                  <strong>Google Workspace</strong>, name it (e.g.{" "}
                  <code>Workspace</code>), set <strong>Customer domain</strong> (e.g.{" "}
                  <code>customer.com</code>), <strong>Delegated admin email</strong>, and
                  paste the SA JSON key.
                </li>
                <li>
                  Save and <strong>Test</strong>. A green "success" with the impersonated
                  admin shown means the delegation is wired up correctly.
                </li>
              </Steps>
              <Note>
                If the test fails with "unauthorized_client" or "access denied," the
                domain-wide delegation step didn't propagate — wait ~10 minutes after
                adding the SA in the Workspace admin and try again, and double-check the
                scopes match exactly.
              </Note>
              <H3>What AeroGauge can do once connected</H3>
              <Bullet>
                <li>Read: users (across the domain, paginated), groups.</li>
                <li>
                  Write (always via AI Actions approval queue):{" "}
                  <code>gworkspace_create_user</code>,{" "}
                  <code>gworkspace_suspend_user</code> (HIGH risk — cuts sign-in),{" "}
                  <code>gworkspace_unsuspend_user</code>,{" "}
                  <code>gworkspace_reset_user_password</code> (HIGH risk),{" "}
                  <code>gworkspace_group_add_member</code>,{" "}
                  <code>gworkspace_group_remove_member</code>.
                </li>
                <li>
                  Passwords (create + reset) are returned in the immediate execute
                  response only — the AIAction.result column strips them so they don't
                  sit in the queue history. Same pattern as M365 mailbox create.
                </li>
              </Bullet>
            </HSection>

            <HSection
              id="wf-ai-cloud-commands"
              title="How do I drive cloud / Meraki / M365 from the AI?"
              intro="The AI proposes; you approve. Same approval-queue pattern used for everything else."
              group="How do I…"
              icon="🤖"
            >
              <p>
                The AI can <em>propose</em> writes against any connected platform — start/stop a
                VM, add a Meraki port-forward, create an M365 mailbox. Each proposal lands in the{" "}
                <Path>AI Actions</Path> queue with the action_type and params; an admin
                approves, the executor runs the SDK call, the result + audit trail are saved.
                There is no "let the AI just do it" path for cloud writes.
              </p>
              <H3>From the AI Terminal</H3>
              <p>
                Open the AI Terminal (sidebar). Issue an API token under Integrations and{" "}
                <code>export RMM_API_TOKEN=ag_...</code>. Then use the Claude CLI:
              </p>
              <code className="block bg-slate-900 text-emerald-300 p-3 my-2 text-xs rounded-lg font-mono shadow-inner whitespace-pre-wrap">
                claude "Restart the prod-web VM for customer Acme. Use the prod Azure
                subscription connector."
                {"\n"}
                claude "Stop the i-0a1b2c3d4 EC2 instance in customer Acme's prod AWS
                account."
                {"\n"}
                claude "Snapshot the data EBS volume vol-0123abcd for Acme's prod AWS
                account before the migration."
                {"\n"}
                claude "Reset the web-prod GCE instance in us-central1-a for Acme's prod
                GCP project."
                {"\n"}
                claude "Suspend jane.doe@acme.com in Acme's Workspace — she left the
                company yesterday."
                {"\n"}
                claude "Set up a port forward on customer Acme's Meraki: TCP 8080 from
                internet1 to 192.168.10.50:80. Call it 'web-admin'."
                {"\n"}
                claude "Create a mailbox jane.doe@acme.com (display 'Jane Doe') in Acme's
                M365. Use a Business Premium license if one is available."
              </code>
              <p>
                Each of those triggers Claude (via MCP) to: (1) discover the right
                connector/network/customer with the read tools (
                <code>list_cloud_connectors</code>, <code>meraki_list_networks</code>,{" "}
                <code>m365_list_skus</code>), and (2) call <code>propose_action</code> with
                the matching <code>action_type</code> and <code>params</code>.
              </p>
              <H3>Approving the action</H3>
              <Steps>
                <li>
                  Open <Path>AI Actions</Path> (sidebar). The proposed action appears with the
                  params Claude chose, the risk tier, and which connector it targets. Each row
                  also identifies the affected endpoint as{" "}
                  <strong>Customer · hostname</strong> (e.g. "Northside Dental · NSD-RECEPTION")
                  rather than a bare device number, so you can see at a glance whose machine it is.
                </li>
                <li>
                  Click <strong>Approve</strong>. The executor runs the SDK call immediately;
                  the result column shows the response (operation accepted, rule applied, user
                  created, etc.). Click <strong>Reject</strong> to discard.
                </li>
                <li>
                  Every approval + execution is in <Path>Audit Log</Path> with the actor, the
                  connector, and the params.
                </li>
              </Steps>
              <Note>
                Initial passwords from <code>m365_create_mailbox</code> are{" "}
                <strong>not</strong> persisted in the action's result column — they're returned
                in the immediate execute response only. Hand them to the user out-of-band; the
                mailbox is set to force-change-on-first-sign-in by default.
              </Note>
            </HSection>

            <HSection
              id="wf-api-token"
              title="How do I mint an API token for integrations / scripts?"
              group="How do I…"
              icon="🗝️"
            >
              <Steps>
                <li>
                  <Path>Integrations</Path> → <strong>Inbound API access</strong>.
                </li>
                <li>
                  Click <strong>+ New token</strong>. Name it (e.g. "CI deploy"). Optionally set
                  expiry in days.
                </li>
                <li>
                  The token (<code>ag_&lt;32-char&gt;</code>) is shown <strong>once</strong>.
                  Copy it immediately into your secret store.
                </li>
                <li>
                  Use it as a bearer token:
                  <code className="block bg-slate-900 text-emerald-300 p-3 my-2 text-xs rounded-lg font-mono shadow-inner">
                    curl -H "Authorization: Bearer ag_..." https://your-rmm/api/v1/devices
                  </code>
                </li>
                <li>
                  The row shows last-used timestamp. Revoke any time via the row's{" "}
                  <strong>Revoke</strong> button.
                </li>
              </Steps>
              <Note>
                API tokens cannot mint other tokens, enroll MFA, or change passwords. Those still
                require an interactive password+MFA login — so a leaked token can't be used to
                escalate the account.
              </Note>
            </HSection>

            <HSection
              id="wf-push"
              title="How do I enable push notifications for critical alerts?"
              group="How do I…"
              icon="📲"
            >
              <Steps>
                <li>
                  Sidebar footer → <Path>Security</Path> → <strong>Push Notifications</strong>{" "}
                  card.
                </li>
                <li>
                  Click <strong>Enable on this device</strong>. Your browser will prompt for
                  notification permission — accept.
                </li>
                <li>
                  Click <strong>Test</strong> to confirm — a test notification should pop up
                  immediately in your OS notification area.
                </li>
                <li>
                  You'll now get pushes for <code>alert.critical</code> events even when the
                  Dashboard tab is closed. Click the notification to jump to the device.
                </li>
                <li>
                  To stop receiving on this device: same page → <strong>Disable</strong>.
                </li>
              </Steps>
              <Note>
                You can enable on multiple devices (laptop, phone, tablet). Each device is listed
                separately under "My subscribed devices." Repeated alerts for the same issue{" "}
                <em>replace</em> earlier ones in the OS shade (no notification storm).
              </Note>
            </HSection>

            <HSection
              id="appliance-setup"
              title="First-run setup wizard"
              intro="How a fresh appliance gets configured before anyone can log in."
              group="Configure"
              icon="🧰"
            >
              <p>
                A freshly-deployed appliance boots into a <strong>first-run setup
                wizard</strong>. Until it's finished, every API route except the wizard
                itself, <code>/health</code>, and public branding is blocked (the backend
                returns <code>503 setup_required</code>), and the UI shows the wizard
                instead of the login screen.
              </p>
              <H3>What the wizard collects</H3>
              <Bullet>
                <li><strong>Admin account</strong> — the first administrator. There is no shipped default password; this account is created here.</li>
                <li><strong>Company &amp; branding</strong> — company name, brand color, support email, and the appliance <strong>timezone</strong> (drives all scheduled jobs).</li>
                <li><strong>Email</strong> — just one field: where to send alerts. There is no mail account to configure. The appliance sends its own mail, and its addresses are derived from the business name above — see the email-to-ticket section.</li>
                <li><strong>License activation</strong> — paste the activation token (and Hub URL + public key) from your provider. Optional/skippable; you can activate later from the License page. See the licensing reference for caps/expiry behavior.</li>
              </Bullet>
              <p>
                Everything here is read <strong>DB-first</strong>, so what you enter takes effect
                immediately — no <code>.env</code> edit or restart required.
              </p>
              <H3>How config is stored</H3>
              <p>
                Wizard values are written to the database (the <code>app_settings</code>
                table), not to <code>.env</code>. The app reads config <strong>DB-first
                with an environment fallback</strong>, so what you set here takes effect
                without editing files. Only the bootstrap secrets (database URL + the
                master encryption keys) stay in the environment and are never settable over
                HTTP.
              </p>
              <Note>
                Existing deployments that predate the wizard are auto-detected at startup
                (any customer on file ⇒ "already set up") so an upgrade never traps you
                behind the wizard. An admin can re-open the wizard later via{" "}
                <code>POST /api/v1/setup/reopen</code>. Changing the timezone takes effect
                after the next backend service restart, since scheduled jobs register their
                timezone at startup.
              </Note>
              <H3>Recovery bundle</H3>
              <p>
                After setup, go to <Path>Settings → Recovery bundle</Path> and export a
                passphrase-encrypted copy of the appliance's master keys (JWT signing + the
                three AES master keys + VAPID). These keys live only on the appliance — if
                it's lost, encrypted data (customer credentials, AI keys, BitLocker escrow)
                is <strong>unrecoverable</strong> without this file. Store the file and its
                passphrase separately and offline.
              </p>
            </HSection>

            <HSection
              id="appliance-license"
              title="Appliance licensing"
              intro="Activation, device caps, expiry, and phone-home."
              group="Configure"
              icon="🔑"
            >
              <p>
                Sidebar → <Path>License</Path> (admin). Paste the activation token from your
                provider (with the Hub URL + public key) to license the appliance. The token
                is verified <strong>offline</strong> against the Hub's public key; a periodic
                <strong> check-in</strong> refreshes the lease and picks up revocation. The
                dashboard shows a license banner with status + device usage.
              </p>
              <H3>Enforcement</H3>
              <Bullet>
                <li><strong>Device cap</strong> — new device enrollment is hard-blocked past <code>max_devices × 1.10</code> (10% grace). Existing devices keep reporting.</li>
                <li><strong>Expiry</strong> — a 14-day read-only grace after the expiry date (mutations blocked, reads allowed), then a hard lock until re-activated.</li>
                <li><strong>Revoked / suspended</strong> — the Hub flips the verdict at the next check-in and the appliance locks (license + auth pages stay reachable to re-activate).</li>
                <li><strong>Unlicensed</strong> — no token applied ⇒ no enforcement (dev / pre-activation appliances run unrestricted).</li>
              </Bullet>
            </HSection>

            <HSection
              id="settings-when"
              title="Settings: what to set & when"
              intro="A quick decision tree of what to change vs leave alone."
              group="Configure"
              icon="⚙️"
            >
              <Bullet>
                <li>
                  <strong>Company name, logo, alert email</strong> — set once on Day 1.
                </li>
                <li>
                  <strong>Device offline threshold</strong> — default 5 min is good. Raise only if
                  your customer has known flaky internet (set to 15 min) to reduce noise.
                </li>
                <li>
                  <strong>Alert thresholds</strong> — see{" "}
                  <a href="#thresholds" className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium">
                    below
                  </a>
                  .
                </li>
                <li>
                  <strong>Patch policy</strong> — see{" "}
                  <a href="#patch-policy" className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium">
                    below
                  </a>
                  .
                </li>
                <li>
                  <strong>SLA policy</strong> — see{" "}
                  <a href="#sla-policy" className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium">
                    below
                  </a>
                  .
                </li>
                <li>
                  <strong>Webhooks</strong> — only when you need Slack/Teams/PagerDuty integration.
                </li>
                <li>
                  <strong>Agent builds</strong> — only when releasing a new agent MSI. See{" "}
                  <a
                    href="#wf-rollout-agent"
                    className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
                  >
                    "Roll out a new agent version"
                  </a>
                  .
                </li>
                <li>
                  <strong>API tokens</strong> — only when integrating an external system.
                </li>
              </Bullet>
            </HSection>

            <HSection
              id="thresholds"
              title="Tuning alert thresholds"
              intro="Decide globally, override per-customer."
              group="Configure"
              icon="🎚️"
            >
              <p>
                Alert thresholds are <strong>info / warning / critical</strong> per metric, plus a{" "}
                <strong>re-alert hours</strong> (how often a still-active alert re-emails).
              </p>
              <Steps>
                <li>
                  Start with the <strong>global</strong> defaults on Settings → Alert Thresholds.
                  They're sensible for most environments.
                </li>
                <li>
                  If a specific customer has a different normal (e.g. a video-editor's machine
                  that pegs CPU during renders), override <em>per customer</em> on Customer detail
                  → Reports → Alert Thresholds.
                </li>
                <li>
                  Binary metrics (fan_health, disk_health, ups_status, psu_status, raid_health)
                  only have the re-alert-hours setting — they don't use numeric thresholds.
                </li>
              </Steps>
              <Tip>
                The threshold registry is auto-discovered from the code. If a new metric is added
                in a future release, it shows up here automatically — no upgrade ritual.
              </Tip>
            </HSection>

            <HSection
              id="patch-policy"
              title="Choosing a patch policy"
              intro="Auto-approve vs manual review."
              group="Configure"
              icon="🧩"
            >
              <p>For each classification (Security, Critical, Recommended, Optional, Drivers):</p>
              <Bullet>
                <li>
                  <strong>Auto-approve</strong> — patches deploy automatically inside the next
                  maintenance window. Fast, lower overhead.
                </li>
                <li>
                  <strong>Manual</strong> — patch sits as "pending approval"; you must approve on
                  the Patch Compliance page before it dispatches. Slower, but you're in control.
                </li>
                <li>
                  <strong>Reject</strong> — patch is suppressed entirely.
                </li>
              </Bullet>
              <p>Typical setup:</p>
              <Bullet>
                <li>
                  Security + Critical → <strong>Auto-approve</strong> (don't delay these).
                </li>
                <li>
                  Recommended → <strong>Manual</strong> (review for known-bad patches).
                </li>
                <li>
                  Optional, Drivers → <strong>Manual</strong> or <strong>Reject</strong>.
                </li>
              </Bullet>
              <p>
                Customers with tighter requirements can override the global policy on their detail
                page → Reports tab.
              </p>
            </HSection>

            <HSection
              id="maintenance"
              title="Maintenance windows"
              group="Configure"
              icon="🌙"
            >
              <p>
                Maintenance windows control <strong>when</strong> approved patches actually
                deploy. The dispatcher checks every 5 minutes; outside the window, deployable
                patches wait.
              </p>
              <Steps>
                <li>
                  Customer detail → <strong>Reports</strong> tab →{" "}
                  <strong>Maintenance Window</strong> card.
                </li>
                <li>
                  Set days-of-week + start/end time + timezone. Default is Sat 02:00–06:00
                  customer-local.
                </li>
                <li>
                  Add a <strong>reboot message</strong> if reboots may happen — shown to end users
                  via the agent's notification.
                </li>
                <li>Save. Next dispatch pass respects the new window.</li>
              </Steps>
            </HSection>

            <HSection id="sla-policy" title="SLA policy" group="Configure" icon="📋">
              <p>
                Set targets in <strong>minutes of active time</strong> per priority. Active time =
                ticket is <code>open</code> or <code>in_progress</code>. <code>on_hold</code>,{" "}
                <code>resolved</code>, <code>closed</code> all pause the clock.
              </p>
              <Steps>
                <li>
                  <Path>HelpDesk → Settings</Path> → <strong>SLA policy (global)</strong>.
                </li>
                <li>
                  For each priority (Low / Normal / High / Urgent) set:
                  <Bullet>
                    <li>
                      <strong>Response target</strong> — first comment / status change to{" "}
                      <code>in_progress</code>.
                    </li>
                    <li>
                      <strong>Resolution target</strong> — total active time to{" "}
                      <code>resolved</code>.
                    </li>
                  </Bullet>
                </li>
                <li>
                  Save. Existing open tickets are evaluated against the new policy on the next
                  pass (5-min interval).
                </li>
                <li>Per-customer override: Customer detail → Reports → SLA Policy.</li>
              </Steps>
            </HSection>

            <HSection
              id="sso-providers"
              title="SSO providers (Entra / Okta / Google)"
              intro="Let staff sign in with your company IdP instead of a local password."
              group="Configure"
              icon="🔑"
            >
              <p>
                AeroGauge speaks OIDC. Anywhere your IdP exposes a{" "}
                <code>.well-known/openid-configuration</code> URL, you can add it as a provider.
                Staff see a "Sign in with X" button on the login page.
              </p>
              <H3>Add a provider</H3>
              <Steps>
                <li>
                  <Path>Settings → Single sign-on (OIDC)</Path> → <strong>Add provider</strong>.
                </li>
                <li>
                  Copy the <strong>redirect URI</strong> shown in the card and paste it into your
                  IdP's app registration. AeroGauge only accepts this exact URI back from the IdP.
                </li>
                <li>
                  Back in AeroGauge, fill in:
                  <Bullet>
                    <li>
                      <strong>Name</strong> — what appears on the Login button (e.g.{" "}
                      <code>Microsoft Entra ID</code>).
                    </li>
                    <li>
                      <strong>Discovery URL</strong> — the IdP's{" "}
                      <code>.well-known/openid-configuration</code> URL. Examples:
                      <Bullet>
                        <li>
                          Entra ID:{" "}
                          <code>
                            https://login.microsoftonline.com/&lt;tenant-id&gt;/v2.0/.well-known/openid-configuration
                          </code>
                        </li>
                        <li>
                          Google Workspace:{" "}
                          <code>https://accounts.google.com/.well-known/openid-configuration</code>
                        </li>
                        <li>
                          Okta:{" "}
                          <code>
                            https://&lt;your-okta-domain&gt;/.well-known/openid-configuration
                          </code>
                        </li>
                      </Bullet>
                    </li>
                    <li>
                      <strong>Client ID / Client secret</strong> — from your IdP's app
                      registration. The secret is stored AES-encrypted; it never appears again in
                      any GET response.
                    </li>
                    <li>
                      <strong>Allowed email domains</strong> — comma-separated. Only users whose
                      IdP-asserted email matches one of these get auto-provisioned. Leave blank to
                      allow any domain (use with care).
                    </li>
                    <li>
                      <strong>Default role for new users</strong> —{" "}
                      <code>technician</code> is the safe default. Promote individuals later via
                      Users.
                    </li>
                    <li>
                      <strong>Auto-provision</strong> — when on, an unknown user who passes the
                      domain check gets a fresh account. When off, the IdP can only sign in users
                      that already exist locally.
                    </li>
                  </Bullet>
                </li>
                <li>Save. The provider becomes available on the next visit to the login page.</li>
              </Steps>
              <H3>How identity is tracked</H3>
              <p>
                AeroGauge uses the IdP-asserted <code>sub</code> claim as the trust anchor — emails
                can change at the IdP without breaking the link. If a user is already known by
                email, signing in via SSO links them; the link refuses if they're already linked
                to a <em>different</em> IdP.
              </p>
              <Note>
                Existing local-password users keep their password access after being linked. You
                can later require SSO-only by editing the user and clearing the password (or
                disabling the local <code>auth/login</code> route at the proxy if you want a hard
                cutover).
              </Note>
            </HSection>

            <HSection
              id="cloud-connectors"
              title="Cloud connectors"
              intro="Per-customer credentials AeroGauge uses to act against Azure, AWS, GCP, and Google Workspace."
              group="Configure"
              icon="☁️"
            >
              <p>
                Each connector is one (customer, platform, name) tuple. Credentials live
                AES-encrypted; the plaintext never leaves request handling. You can have
                multiple connectors of the same platform per customer (e.g. <code>Prod
                subscription</code> + <code>DR subscription</code>).
              </p>
              <H3>Adding a connector</H3>
              <p>
                Step-by-step for each platform:{" "}
                <a href="#wf-connect-azure" className="text-indigo-600 dark:text-indigo-400 hover:underline">Azure</a>{", "}
                <a href="#wf-connect-aws" className="text-indigo-600 dark:text-indigo-400 hover:underline">AWS</a>{", "}
                <a href="#wf-connect-gcp" className="text-indigo-600 dark:text-indigo-400 hover:underline">GCP</a>{", "}
                <a href="#wf-connect-gworkspace" className="text-indigo-600 dark:text-indigo-400 hover:underline">Google Workspace</a>.
              </p>
              <H3>Security model</H3>
              <Bullet>
                <li>
                  Direct REST writes against connectors (the <Path>Cloud</Path> tab buttons,
                  the <code>/cloud-connectors/.../start</code> endpoints) require{" "}
                  <strong>admin</strong> role and are audit-logged.
                </li>
                <li>
                  AI-initiated writes (via MCP or the in-app action engine) go through{" "}
                  <Path>AI Actions</Path> approval. The AI cannot bypass it.
                </li>
                <li>
                  Test-connection results are cached on the row (<code>last_test_status</code>,{" "}
                  <code>capabilities</code>) so the dashboard can show stale-creds without
                  re-calling the IdP every page load.
                </li>
              </Bullet>
              <H3>What's not in v1</H3>
              <Bullet>
                <li>Multi-region AWS inventory (today the connector lists one region — its <code>default_region</code>; change it on the connector and re-list to see another).</li>
                <li>Workspace Drive / Calendar / Gmail admin (separate Directory scopes; coming when an MSP asks).</li>
                <li>Workspace license assignment (a separate API surface).</li>
                <li>Interactive cloud-shell mode in the AI Terminal (need the az/aws/gcloud CLIs).</li>
                <li>Cost / billing read tools.</li>
                <li>Bulk operations (start/stop all VMs in an RG, etc.).</li>
              </Bullet>
            </HSection>

            <HSection
              id="ref-dashboard"
              title="Dashboard"
              intro="What the home page shows."
              group="Reference"
              icon="📍"
            >
              <Bullet>
                <li>
                  <strong>Summary stats</strong> — customer / device / open ticket / active alert
                  counts.
                </li>
                <li>
                  <strong>SLA at risk</strong> — tickets nearing or past SLA breach.
                </li>
                <li>
                  <strong>Health snapshot</strong> — active alerts (critical / warning / info),
                  top 5 offline devices, failed self-heals in last 24h, unreachable network
                  devices. Collapses to "All clear" when every section is empty.
                </li>
                <li>Refreshes every 30 seconds.</li>
              </Bullet>
            </HSection>

            <HSection
              id="ref-devices"
              title="Devices"
              intro="What's on the inventory page and device detail."
              group="Reference"
              icon="💻"
            >
              <p>
                Inventory list: status pill, hostname, customer, OS, agent version, last-seen,
                identity badge (AD / Entra / Hybrid / Workgroup).
              </p>
              <p>Device detail tabs:</p>
              <Bullet>
                <li>
                  <strong>Overview</strong> — IP, OS, identity, agent version.
                </li>
                <li>
                  <strong>Metrics</strong> — CPU, memory, disk, temps, UPS, disk I/O latency,
                  RAID, PSU.
                </li>
                <li>
                  <strong>Inventory</strong> — installed software, hardware specs.
                </li>
                <li>
                  <strong>Patches</strong> — install history, pending approvals.
                </li>
                <li>
                  <strong>Alerts</strong> — active + resolved, with timestamps.
                </li>
                <li>
                  <strong>History</strong> — event log, status transitions.
                </li>
                <li>
                  <strong>BitLocker</strong> — volume state; admin sees recovery key reveal.
                </li>
                <li>
                  <strong>Warranty</strong> — vendor-pulled service tag, end date, status pill.
                </li>
                <li>
                  <strong>Remote shell</strong> — admin-only live PowerShell terminal.
                </li>
              </Bullet>
            </HSection>

            <HSection
              id="ref-hw"
              title="Hardware signals"
              intro="What each metric represents and its default thresholds."
              group="Reference"
              icon="🌡️"
            >
              <Bullet>
                <li>
                  <strong>Disk used %</strong> — info 80 / warn 85 / critical 95.
                </li>
                <li>
                  <strong>Memory used %</strong> — 80 / 85 / 90.
                </li>
                <li>
                  <strong>CPU used %</strong> — 80 / 85 / 90. Warning upgrades to critical once
                  sustained ≥10 min.
                </li>
                <li>
                  <strong>Disk I/O latency</strong> (ms) — 30 / 60 / 100.
                </li>
                <li>
                  <strong>CPU temp</strong> — 80°C / 85°C / 90°C.
                </li>
                <li>
                  <strong>Chassis temp</strong> — 45°C / 50°C / 55°C.
                </li>
                <li>
                  <strong>UPS health %</strong> (reverse) — info &lt;50 / warn &lt;30 / critical
                  &lt;15.
                </li>
                <li>
                  <strong>UPS status</strong> — on_battery → warn, replace → critical.
                </li>
                <li>
                  <strong>PSU status</strong> — redundancy loss → critical, single bad PSU → warn.
                </li>
                <li>
                  <strong>RAID</strong> — any degraded → warn, any failed → critical.
                </li>
                <li>
                  <strong>SMART</strong> + <strong>battery</strong> + <strong>fan</strong> —
                  binary states.
                </li>
              </Bullet>
              <p className="text-sm text-slate-500 dark:text-slate-400 italic">
                Hardware with no sensor (e.g. workstations with no RAID controller) returns an
                empty list and generates no false alerts.
              </p>
            </HSection>

            <HSection
              id="ref-tickets"
              title="Tickets"
              intro="Lifecycle, SLA, fields."
              group="Reference"
              icon="🎫"
            >
              <p>
                Statuses: <code>open</code> → <code>in_progress</code> → <code>resolved</code> →{" "}
                <code>closed</code>, with <code>on_hold</code> as a side branch.
              </p>
              <p>Priorities: Low / Normal / High / Urgent — drive the SLA targets.</p>
              <p>
                Fields: subject, description, customer, optional device link, assignee, priority,
                status, time entries, comments.
              </p>
            </HSection>

            <HSection
              id="ref-m365"
              title="Microsoft 365 / Entra"
              group="Reference"
              icon="☁️"
            >
              <Bullet>
                <li>
                  Entra users, group memberships, license assignments, subscriptions for the
                  configured tenant.
                </li>
                <li>
                  Hybrid Identity badge on every device (AD / Entra / Hybrid / Workgroup) —
                  computed from <code>dsregcmd /status</code> on the agent.
                </li>
                <li>
                  Phase 2 (action routing through Graph for Entra-only users) and Phase 3 (full
                  Entra Users page) are in development; today only detection + the badge are live.
                </li>
              </Bullet>
            </HSection>

            <HSection id="ref-network" title="Network devices" group="Reference" icon="🌐">
              <p>
                Routers, switches, firewalls, UPS units. Each has a polling profile (ICMP +
                optional SNMP) and reports up/down + vendor-specific health. Unreachable devices
                surface on the Dashboard.
              </p>
            </HSection>

            <HSection id="ref-portal" title="Customer portal" group="Reference" icon="🏠">
              <p>
                Customer-facing UI at <code>/portal</code>. Customer users see only their own data
                — cross-tenant requests return 404. Pages: Dashboard, Tickets, Devices, Invoices,
                Runbooks (customer-visible only), Security.
              </p>
            </HSection>

            <HSection
              id="ref-audit"
              title="Audit log (Admin only)"
              group="Reference"
              icon="🔍"
            >
              <p>
                System-wide record of meaningful actions. Filter by action, actor, customer,
                target type, result, time range. Expand a row for structured details (with
                before/after diff when applicable). CSV export streams the full filtered result.
              </p>
            </HSection>

            <HSection
              id="ref-users"
              title="Users & roles (Admin only)"
              group="Reference"
              icon="👥"
            >
              <p>Three built-in roles:</p>
              <Bullet>
                <li>
                  <strong>Admin</strong> — everything, including Settings, Users, the Audit Log,
                  licensing and backups. Cannot be restricted.
                </li>
                <li>
                  <strong>Technician</strong> — the everyday operational role. Works across every
                  customer; cannot manage staff or global settings.
                </li>
                <li>
                  <strong>Viewer</strong> — read-only.
                </li>
              </Bullet>
              <p>
                Those three are coarse on their own, so{" "}
                <Path>Admin → Roles &amp; access</Path> lets you switch individual features off for
                Technician and Viewer — see <a href="#roles-access" className="text-blue-600 dark:text-blue-400 hover:underline">Roles &amp; access</a>.
              </p>
              <p>
                Admins can also unlock throttled accounts, force a password change at next sign-in,
                and end somebody else's session.
              </p>
            </HSection>

            <HSection id="ai" title="AI Mode & Action Engine" group="Configure" icon="⚡">
              <p>
                AI Mode lets your MSP plug in any supported AI provider (Anthropic Claude, OpenAI,
                Azure OpenAI, Google Gemini, Ollama, or any OpenAI-compatible endpoint) and give
                your techs a fast assist layer over the whole platform — plus an opt-in action
                engine that can propose and (with approval) execute real repairs.
              </p>

              <H3>How it works</H3>
              <Bullet>
                <li>
                  <strong>Settings → AI Settings.</strong> Add one or more provider configs. Each
                  carries an encrypted API key (AES-256-GCM, keyed off{" "}
                  <code>AI_PROVIDER_KEY</code> in <code>.env</code>). Flip{" "}
                  <em>Use as default</em> on the one you want the platform to call.
                </li>
                <li>
                  <strong>Master AI Mode toggle.</strong> Top of the same page. When this is OFF
                  the AI subsystem is completely inert — no outbound calls, no proposals — even
                  if the feature toggles below are on. The sidebar logo shows{" "}
                  <code className="bg-violet-500 text-white px-1 rounded text-[10px]">AI ON</code>{" "}
                  when active.
                </li>
                <li>
                  <strong>Per-feature toggles.</strong>
                  <ul className="list-disc list-inside ml-4 mt-1">
                    <li>
                      <em>Ticket Assist</em> — summarize threads, suggest replies, classify
                      priority/category. Shows up as a violet panel on every ticket.
                    </li>
                    <li>
                      <em>Alert Summarization</em> — plain-English alert explanations + suggested
                      next action. Wired into the alerts UI.
                    </li>
                    <li>
                      <em>Action Engine</em> — AI proposes repairs (restart a service, run a
                      script, reboot, install a patch, AD actions). Surfaces as proposed actions
                      that route through the AI Actions queue.
                    </li>
                  </ul>
                </li>
              </Bullet>

              <H3>Risk tiers and approval flow</H3>
              <p>Every proposed action carries a risk tier that drives the approval flow:</p>
              <Bullet>
                <li>
                  <strong>LOW</strong> (read-only summaries, classify, diagnose) — auto-executes
                  when AI Mode + the relevant feature toggle are on. Recorded with full audit
                  trail.
                </li>
                <li>
                  <strong>MEDIUM</strong> (restart service, run script, winget install/upgrade,
                  post ticket comment, change priority) — needs one click in the AI Actions queue
                  to approve, then executes.
                </li>
                <li>
                  <strong>HIGH</strong> (reboot, shutdown, install patch, any AD action, ticket
                  close) — requires an explicit approval note. The API rejects an approve call
                  with no note for HIGH-risk actions.
                </li>
              </Bullet>

              <H3>What gets audited</H3>
              <p>
                Every state transition writes to <code>audit_logs</code>:{" "}
                <code>ai_action_approve</code>, <code>ai_action_reject</code>,{" "}
                <code>ai_action_execute</code>, <code>ai_provider_create</code>,{" "}
                <code>ai_provider_update</code>, <code>ai_provider_delete</code>,{" "}
                <code>ai_flags_update</code>. Filter by these on{" "}
                <Path>Audit Log</Path>.
              </p>

              <H3>The AI Actions queue</H3>
              <p>
                Sidebar → <Path>AI Actions</Path>. Filter by status. Each card shows the proposed
                action, parameters, AI rationale, provider/model, and the linked target
                (device/ticket/alert). Three buttons on proposed actions: <em>Approve + Execute</em>{" "}
                (single click), <em>Approve only</em> (queue for later execution), <em>Reject</em>.
              </p>

              <H3>Adding a provider</H3>
              <Bullet>
                <li>
                  <strong>Anthropic Claude</strong> — paste an{" "}
                  <code>sk-ant-...</code> key. Default model is{" "}
                  <code>claude-opus-4-7</code> (1M-context).
                </li>
                <li>
                  <strong>OpenAI</strong> — paste an <code>sk-...</code> key. Default model{" "}
                  <code>gpt-4o</code>.
                </li>
                <li>
                  <strong>Azure OpenAI</strong> — set Base URL to your resource endpoint and add
                  to Extra config:{" "}
                  <code>{`{"api_version": "2024-06-01", "deployment": "my-gpt4o"}`}</code>
                </li>
                <li>
                  <strong>Google Gemini</strong> — paste an AI Studio key. Default model{" "}
                  <code>gemini-2.0-flash</code>.
                </li>
                <li>
                  <strong>Ollama (local)</strong> — leave API key blank, Base URL{" "}
                  <code>http://localhost:11434</code>. Default model <code>llama3.1</code>.
                </li>
                <li>
                  <strong>Custom (OpenAI-compatible)</strong> — any vLLM, OpenRouter, Together,
                  Groq, Fireworks endpoint. Set Base URL and the model name they advertise.
                </li>
              </Bullet>
              <p>Use the <em>Test connection</em> button to smoke-test before saving.</p>

              <H3>Security notes</H3>
              <Bullet>
                <li>
                  API keys are stored as AES-256-GCM ciphertext with per-row nonces. The master
                  key (<code>AI_PROVIDER_KEY</code>) is separate from{" "}
                  <code>CREDENTIAL_VAULT_KEY</code> so a compromise of one doesn't leak the
                  other.
                </li>
                <li>
                  Plaintext keys never appear in any API response. <em>Edit</em> with the key
                  field blank keeps the existing ciphertext untouched.
                </li>
                <li>
                  Server-side <code>ai_provider_create/update/delete</code> and{" "}
                  <code>ai_flags_update</code> are admin-only.
                </li>
                <li>
                  The AI is given the smallest context required for each task (one ticket, one
                  alert, one device). It cannot enumerate across the platform.
                </li>
              </Bullet>
            </HSection>


            <HSection
              id="ai-correlation"
              title="AI alert correlation"
              group="Reference"
              icon="🔗"
            >
              <p>
                When a disk fills, three different metrics fire (used %, I/O latency, SMART
                health). When a server overheats, four fire (CPU temp, chassis temp, fan, CPU
                throttle). Without correlation, techs page through 8 alerts that are really one
                incident.
              </p>

              <H3>How it groups</H3>
              <Bullet>
                <li>
                  Open alerts on the same <strong>device</strong> sharing a{" "}
                  <strong>metric class</strong> (disk / memory / cpu / thermal / power /
                  storage_hw) get clustered.
                </li>
                <li>
                  The oldest alert in the cluster becomes the <strong>parent</strong>;
                  newer ones get <code>correlation_parent_id</code> pointing at it.
                </li>
                <li>
                  Only alerts created within a <strong>2-hour window</strong> cluster together —
                  stuck stale alerts don't suppress fresh symptoms.
                </li>
                <li>
                  When all siblings resolve, parent reverts to a standalone alert automatically.
                </li>
              </Bullet>

              <H3>UI behavior</H3>
              <p>
                The Active Alerts dashboard hides children by default. Parents show a
                <code>+N</code> badge with the cluster size; clicking it expands the children.
                Pass <code>?include_correlated=true</code> on{" "}
                <code>GET /api/v1/alerts</code> for the flat list (used by reports + custom
                queries).
              </p>

              <H3>Cadence</H3>
              <p>
                The clustering pass runs every <strong>5 minutes</strong>. Heuristic-only — no
                LLM tokens spent. AI enrichment ("describe this cluster in one sentence") can
                layer on later without redoing the math.
              </p>
            </HSection>

            <HSection
              id="ai-patch-risk"
              title="AI patch risk scoring"
              group="Reference"
              icon="⚠️"
            >
              <p>
                Every pending patch gets a <strong>0-100 risk score</strong> so MSPs can hold
                flagged updates for human review while letting safe ones flow through
                maintenance windows.
              </p>

              <H3>Risk bands</H3>
              <Bullet>
                <li>
                  <strong>0-29 low</strong> — auto-install safe (default)
                </li>
                <li>
                  <strong>30-59 medium</strong> — auto-install if policy permits; surfaced in UI
                </li>
                <li>
                  <strong>60-79 high</strong> — held even when policy would auto-approve
                </li>
                <li>
                  <strong>80-100 critical</strong> — blocked from auto-install entirely
                </li>
              </Bullet>

              <H3>What raises the score</H3>
              <Bullet>
                <li>+30 released within 7 days (MSFT often pulls/respins early)</li>
                <li>+20 driver / firmware category (drivers regress more than software)</li>
                <li>+20 KB on the known-bad list (community-flagged historical issues)</li>
                <li>+15 cumulative update (large blast radius)</li>
                <li>+10 released &lt;24h ago (stacks with the 7-day rule)</li>
                <li>+10 requires reboot</li>
                <li>+5 preview / optional update</li>
                <li>-15 definition update (signature files; low risk)</li>
                <li>-10 already installed cleanly on 80%+ of the customer's fleet</li>
              </Bullet>

              <H3>Policy gate</H3>
              <p>
                Each patch policy has a <code>max_auto_install_risk</code> integer (default
                100 = no gate). Set it to 60 and any auto-approved patch scoring &gt;60 stays
                queued without installing until a tech overrides it. Manual approvals always
                bypass the gate.
              </p>

              <H3>Cadence</H3>
              <p>
                Re-scored daily at <strong>04:00 ET</strong>. Risk bands move on the day-scale
                (patch ages out of the new-release window, fleet install signal grows), not the
                hour-scale.
              </p>
            </HSection>

            <HSection
              id="integrations-reference"
              title="Every integration & what it does"
              group="Reference"
              icon="🧩"
              intro="The complete list, read from the appliance itself - what each integration does, what it needs from you, and where it stops."
            >
              <IntegrationReference />
            </HSection>

            <HSection
              id="integrations"
              title="Vendor integrations"
              group="Configure"
              icon="🔌"
            >
              <p>
                Security, backup and DNS-filtering products can be connected so their state shows
                up alongside everything else. Each connector runs hourly, pulls the vendor's own
                view, and surfaces it on the device card or the customer page. The connectors
                configured today are listed under <Path>Integrations → All integrations</Path> —
                the setup below covers the ones that ship in the box.
              </p>

              <H3>Setup pattern</H3>
              <Steps>
                <li>
                  Go to <Path>Settings → Integrations</Path>. Click <em>Configure</em> on the
                  vendor row.
                </li>
                <li>
                  Paste vendor credentials. Required fields per vendor:
                  <ul className="list-disc pl-5 mt-1 space-y-1">
                    <li>
                      <strong>Huntress</strong>: API Key ID + API Key Secret (Huntress admin
                      → Settings → API Keys).
                    </li>
                    <li>
                      <strong>Acronis</strong>: Datacenter URL + Client ID + Client Secret (Cyber
                      Cloud admin → Settings → API Clients).
                    </li>
                    <li>
                      <strong>DNSFilter</strong>: API Key (DNSFilter admin → API). Organization
                      ID is optional if you only have one DNSFilter org.
                    </li>
                  </ul>
                </li>
                <li>
                  Click <em>Test</em> to verify auth works. Hit <em>Sync now</em> to do the first
                  full pull.
                </li>
                <li>
                  Devices are matched to customers <strong>by hostname</strong>. There is no
                  per-customer ID to fill in: the screen that asked for one named three
                  vendors specifically and has been removed.
                </li>
              </Steps>

              <H3>What the sync writes</H3>
              <Bullet>
                <li>
                  <strong>Huntress</strong> → <code>devices.edr_status</code>
                  (secure / at_risk / breached / unmanaged) +{" "}
                  <code>huntress_agent_id</code>. The Device detail page shows a colored EDR
                  badge.
                </li>
                <li>
                  <strong>Acronis</strong> → <code>devices.backup_status</code>
                  (success / partial / failed / running / no_recent / never / unmanaged) +{" "}
                  <code>backup_last_run_at</code> + <code>backup_last_success_at</code>. Device
                  detail page shows a Backup badge with "Nh ago" stamp.
                </li>
                <li>
                  <strong>DNSFilter</strong> → cached on the integration row's
                  <code>last_sync_summary</code> (per-customer policy + 24h query/block counts).
                  Customer-page view surfaces this without re-hitting the vendor API on every
                  pageload.
                </li>
              </Bullet>

              <H3>Cadence + rate-limit notes</H3>
              <p>
                Every vendor sync runs <strong>hourly</strong>. Huntress documents 60 req/min;
                Acronis tokens are short-lived (~60 min) so we re-mint on each sync. DNSFilter's
                /stats endpoints have shifted twice in two years — partial sync failures land in
                the integration row's <code>last_sync_error</code> with the exact HTTP
                response.
              </p>

              <Note>
                Credentials are stored as AES-256-GCM ciphertext using{" "}
                <code>CREDENTIAL_VAULT_KEY</code>. Plaintext is never returned by any API. The{" "}
                <em>Reconfigure</em> form always shows blank fields — re-enter everything to
                update, or hit <em>Disconnect</em> to wipe.
              </Note>

              <Tip>
                The vendor adapters are built against documented APIs but cannot be validated
                against live tenants from the dev environment — the first MSP to plug in real
                credentials is the real validator. If a sync starts erroring, the exact HTTP
                error from the vendor lands in the Integrations card; that's usually enough to
                diagnose (auth fail, endpoint moved, payload shape drift).
              </Tip>
            </HSection>

            <HSection
              id="ai-auto-close"
              title="AI ticket auto-close"
              group="Reference"
              icon="✅"
            >
              <p>
                When the AI successfully runs an action against a ticket AND the device stays
                quiet through the customer's cool-off window, the ticket auto-closes with a
                polite customer email explaining what happened.
              </p>

              <H3>Per-customer opt-in</H3>
              <p>
                Disabled by default. Enable on the Customer detail page (
                <code>ai_auto_close_enabled = true</code>), tune the cool-off window
                (<code>ai_auto_close_cool_off_hours</code>, default 24). Customers who don't opt
                in see no behavior change.
              </p>

              <H3>When a ticket qualifies</H3>
              <Bullet>
                <li>
                  AI action executed successfully and is linked to the ticket
                  (<code>ai_actions.ticket_id</code>).
                </li>
                <li>Cool-off window has elapsed.</li>
                <li>
                  Status is still <code>open</code> or <code>in_progress</code> (manually
                  resolved tickets are left alone).
                </li>
                <li>No customer reply since the AI ran (resets the timer).</li>
                <li>
                  No new alert on the same device since the AI ran (failed fix → don't claim
                  success).
                </li>
              </Bullet>

              <H3>What gets sent</H3>
              <p>
                A final AI-authored TicketComment summarizes the action and outcome; the ticket
                is closed; the customer gets a "Ticket #N Resolved" email via the donotreply
                mailbox. Replying to that email reopens the ticket (email-to-ticket pipeline
                handles it).
              </p>

              <H3>Cadence</H3>
              <p>
                Runs hourly. Closing a ticket 23h vs 24h after cool-off boundary is a non-issue;
                sub-hour precision isn't worth the scheduler load.
              </p>
            </HSection>

            <HSection
              id="email-intake"
              title="Email-to-ticket intake"
              group="Configure"
              icon="📥"
            >
              <p>
                Inbound support email auto-creates tickets. Replies that carry{" "}
                <code>[Ticket #N]</code> or <code>[#N]</code> in the subject become comments on the
                existing ticket and reopen it if closed.
              </p>
              <p>
                <strong>This appliance is its own mail server.</strong> It is the MX for its own
                subdomain and receives on port 25 directly — there is no mailbox to log into, no
                provider account, and no shared relay in the path. Outbound leaves the appliance
                the same way, DKIM-signed. Nothing depends on Microsoft 365, Google or any hosted
                provider.
              </p>
              <H3>Addresses are derived, not typed</H3>
              <p>
                Every address follows the business name entered at setup — its{" "}
                <em>slug</em> — so no one configures a mailbox:
              </p>
              <Bullet>
                <li>
                  <code>helpdesk@&#123;slug&#125;.aerogauge.net</code> — inbound support queue, and
                  the From on ticket mail so a reply threads onto the ticket by itself.
                </li>
                <li>
                  <code>alerts@&#123;slug&#125;.aerogauge.net</code> — monitoring notifications,
                  separated so a recipient can mute the noisy stream without losing ticket mail.
                </li>
                <li>
                  <code>noreply@&#123;slug&#125;.aerogauge.net</code> — invites, scheduled reports,
                  billing. <code>Reply-To</code> is the helpdesk queue on all of it.
                </li>
              </Bullet>
              <H3>Setup</H3>
              <Bullet>
                <li>
                  <Path>HelpDesk → Mail setup</Path> walks the whole thing: the derived address, the
                  one MX record to add, opening inbound port 25, who gets notified, filtering, and
                  the outbound SPF / DKIM / PTR records. It checks DNS against public resolvers, not
                  the local one, so a split-horizon answer from your own DC can't make a broken
                  record look fine.
                </li>
                <li>
                  <Path>HelpDesk → Email intake</Path> is the log: what every inbound message became
                  (created / appended / dropped / blocked / error), plus the blocked-sender rules.
                  The status banner names the live route.
                </li>
                <li>
                  An MSP that wants mail on its own domain instead can override the domain or the
                  full helpdesk address; a white-labelled deployment isn't forced onto an
                  aerogauge.net address in front of its own customers.
                </li>
              </Bullet>
              <H3>Sender → customer mapping</H3>
              <Bullet>
                <li>
                  <strong>Exact match</strong> on{" "}
                  <code>customers.contact_email</code> or <code>customers.billing_email</code>.
                </li>
                <li>
                  <strong>Domain match</strong>: sender's email domain == <code>customers.domain</code>.
                </li>
                <li>
                  No match → the email is <em>dropped</em> (logged with action=dropped). Set the
                  customer's domain/contact email and re-poll, or paste the body manually as a
                  new ticket.
                </li>
              </Bullet>
              <H3>Dedup</H3>
              <p>
                Every processed message writes an <code>incoming_emails</code> row keyed off{" "}
                <code>Message-ID</code>. A redelivery or retry carrying the same Message-ID is
                skipped cleanly.
              </p>
            </HSection>

            <HSection
              id="wf-spiceworks"
              title="Migrate from Spiceworks"
              group="How do I…"
              icon="📥"
            >
              <p>
                Spiceworks Cloud Help Desk has no public ticket API, so the migration runs through
                CSV exports. The importer preserves your existing AeroGauge ticket numbering and
                stores the legacy Spiceworks number in a dedicated{" "}
                <code>tickets.legacy_spiceworks_id</code> column for traceability.
              </p>

              <H3>Step 1 — Export from Spiceworks</H3>
              <Steps>
                <li>
                  Sign in to your Spiceworks Cloud Help Desk (e.g.{" "}
                  <code>https://&lt;your-org&gt;.on.spiceworks.com</code>).
                </li>
                <li>
                  Go to <Path>Tickets</Path>, apply a filter (most shops only migrate{" "}
                  <em>open</em> tickets and leave Spiceworks read-only for closed history — keeps
                  scope small and skips the attachment problem).
                </li>
                <li>
                  Click <em>Export → CSV</em>. Save the file (this is the <strong>tickets CSV</strong>).
                </li>
                <li>
                  Optionally run the export again on the Comments/Replies view to get the{" "}
                  <strong>comments CSV</strong>. The importer accepts either one or both.
                </li>
              </Steps>

              <Note>
                Attachments aren't in the CSV export. If a handful of historical tickets have
                critical attachments, download them manually from Spiceworks first and re-attach
                in AeroGauge after import.
              </Note>

              <H3>Step 2 — Pick a customer-mapping strategy</H3>
              <Bullet>
                <li>
                  <strong>Single customer</strong> (simplest, common for internal-IT shops): pass{" "}
                  <code>--customer-id N</code> and every ticket lands on that customer.
                </li>
                <li>
                  <strong>Per-organization map</strong>: write a JSON file mapping Spiceworks{" "}
                  <em>Organization</em> names to AeroGauge customer IDs, pass{" "}
                  <code>--customer-map orgs.json</code>. Example:{" "}
                  <code>{"{ "}"Acme Corp"{": "}5, "Beta LLC"{": "}7{" }"}</code>.
                </li>
                <li>
                  <strong>Mix</strong>: combine <code>--customer-map</code> with{" "}
                  <code>--default-customer-id</code> as a catch-all for any organization the map
                  doesn't cover.
                </li>
              </Bullet>

              <H3>Step 3 — Dry-run first</H3>
              <p>
                Always preview before committing. Dry-run reads the CSVs end-to-end, reports
                exactly what would be created, and rolls back — zero DB writes:
              </p>
              <pre className="bg-slate-900 text-slate-100 text-xs font-mono p-3 rounded-lg overflow-x-auto">
{`python3 -m scripts.import_spiceworks \\
  --tickets ./spiceworks-tickets.csv \\
  --comments ./spiceworks-comments.csv \\
  --customer-id 5 \\
  --dry-run`}
              </pre>

              <H3>Step 4 — Commit the import</H3>
              <p>Drop <code>--dry-run</code> and re-run the same command:</p>
              <pre className="bg-slate-900 text-slate-100 text-xs font-mono p-3 rounded-lg overflow-x-auto">
{`python3 -m scripts.import_spiceworks \\
  --tickets ./spiceworks-tickets.csv \\
  --comments ./spiceworks-comments.csv \\
  --customer-id 5`}
              </pre>

              <H3>What the importer maps</H3>
              <Bullet>
                <li>
                  <strong>Ticket #</strong> → <code>legacy_spiceworks_id</code> (preserved, unique).
                  Your new AeroGauge ticket numbers keep their existing sequence — Spiceworks IDs
                  are <em>stored</em>, not used as primary keys.
                </li>
                <li>
                  <strong>Summary / Description</strong> → <code>title</code> / <code>description</code>.
                  Spiceworks <em>Assigned To</em> (a display name we can't reliably map to a
                  AeroGauge User) is appended to the description as a note.
                </li>
                <li>
                  <strong>Status</strong> → normalized to{" "}
                  <code>open / in_progress / on_hold / resolved / closed</code>. Anything unknown
                  falls back to <code>open</code>.
                </li>
                <li>
                  <strong>Priority</strong> → <code>low / medium / high / urgent</code>. Numeric
                  priorities (1-5) are mapped sensibly.
                </li>
                <li>
                  <strong>Created By / Email</strong> → an inactive{" "}
                  <code>customer_users</code> shell row is created so the portal user can be
                  invited later. Re-running won't double up — shells are deduplicated by email.
                </li>
                <li>
                  <strong>Created At / Closed At</strong> → preserved verbatim onto{" "}
                  <code>created_at</code> / <code>resolved_at</code> / <code>closed_at</code> so
                  historical timestamps are accurate.
                </li>
                <li>
                  <strong>Comments</strong> joined by ticket # → <code>ticket_comments</code> with{" "}
                  <code>author_username</code> set to the Spiceworks author's name.
                </li>
              </Bullet>

              <H3>Idempotency</H3>
              <p>
                Safe to re-run. Tickets are looked up by <code>legacy_spiceworks_id</code> and
                skipped if already imported; comments are deduplicated by{" "}
                <code>(ticket_id, body, created_at)</code>. The end-of-run summary tells you
                exactly what was new, skipped as dupe, or unmapped.
              </p>

              <Tip>
                After the import, open any migrated ticket. The Spiceworks ticket # lives in the
                <code>legacy_spiceworks_id</code> column — surface it in your own ticket view via
                the API or a custom report if you want it visible to techs.
              </Tip>
            </HSection>

            <HSection
              id="m365-tenants"
              title="Multi-tenant Microsoft 365"
              group="Configure"
              icon="☁️"
            >
              <p>
                Each customer can have its own M365 app registration. The sync service iterates
                over every active tenant row and writes <code>m365_users</code> /{" "}
                <code>m365_subscriptions</code> scoped to that customer.
              </p>
              <H3>Adding a tenant</H3>
              <Bullet>
                <li>
                  Register an Azure AD application in the customer's tenant with these{" "}
                  <em>Application</em> permissions:{" "}
                  <code>User.Read.All</code>, <code>Organization.Read.All</code>,{" "}
                  <code>UserAuthenticationMethod.Read.All</code>. Tenant admin grants consent.
                </li>
                <li>
                  In <Path>M365 Tenants</Path> click <em>+ Add tenant</em>. Pick the customer,
                  paste tenant ID, client ID, client secret.
                </li>
                <li>
                  Click <em>Sync now</em>. Users + subscriptions populate; per-row status shows
                  last sync result (success/failure with error message).
                </li>
              </Bullet>
              <H3>Backward compatibility</H3>
              <p>
                If no rows exist in the tenant table AND the legacy <code>M365_TENANT_ID</code>{" "}
                /<code>CLIENT_ID</code>/<code>CLIENT_SECRET</code> env vars are populated, the
                old single-tenant flow runs with <code>customer_id=NULL</code>. Migrate by adding
                a row for your own MSP tenant, then remove the env vars.
              </p>
              <H3>Encryption</H3>
              <p>
                Client secrets stored as AES-256-GCM ciphertext via <code>CREDENTIAL_VAULT_KEY</code>.
                Plaintext never appears in any API response.
              </p>
            </HSection>

            <HSection
              id="capacity-forecast"
              title="Capacity forecasting"
              group="Reference"
              icon="📈"
            >
              <p>
                Linear-regression projections over daily metric snapshots. Lets you see which
                disks (or RAM / CPU) are trending toward saturation BEFORE they hit the alert
                threshold.
              </p>
              <H3>How it works</H3>
              <Bullet>
                <li>
                  Daily cron at 03:30 ET writes a row to <code>device_metric_snapshots</code>{" "}
                  for each (device, metric) — <code>disk_used_pct</code>,{" "}
                  <code>memory_used_pct</code>, <code>cpu_used_pct</code>.
                </li>
                <li>
                  The forecaster fits a least-squares line over the last 90 days (window
                  configurable per call) and solves for when the value will hit a target
                  threshold.
                </li>
                <li>
                  Minimum 3 sample points spanning at least 7 days — below that we return{" "}
                  <code>insufficient_data</code> rather than a noisy forecast.
                </li>
                <li>
                  Old snapshots past 400 days are pruned in the same daily job.
                </li>
              </Bullet>
              <H3>Where to see it</H3>
              <Bullet>
                <li>
                  <Path>Dashboard</Path> shows "disks projected to fill in &lt;90 days" widget.
                </li>
                <li>
                  API: <code>GET /api/v1/forecast/device/{"{id}"}</code> for single-device,{" "}
                  <code>GET /api/v1/forecast/at-risk</code> for fleet-wide top-N.
                </li>
                <li>
                  Force-snapshot (admin only): <code>POST /api/v1/forecast/snapshot</code>.
                </li>
              </Bullet>
            </HSection>

            <HSection
              id="meshcentral"
              title="MeshCentral remote desktop"
              group="Configure"
              icon="🖥"
            >
              <p>
                Embedded remote desktop via{" "}
                <a
                  href="https://meshcentral.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 dark:text-blue-400 underline"
                >
                  MeshCentral
                </a>
                . The MSP runs MeshCentral as a separate service; AeroGauge generates the
                deep-link URLs and queues agent installs.
              </p>

              <H3>One-time MeshCentral setup</H3>
              <Bullet>
                <li>
                  Install MeshCentral on its own host:{" "}
                  <code>
                    mkdir mesh && cd mesh && npm install meshcentral && node node_modules/meshcentral
                  </code>
                  . First run creates <code>config.json</code> and a self-signed cert.
                </li>
                <li>
                  Edit <code>config.json</code>:
                  <ul className="list-disc list-inside ml-4 mt-1">
                    <li>
                      Set <code>cert</code> to your real FQDN (e.g.{" "}
                      <code>mesh.example.com</code>).
                    </li>
                    <li>
                      Under <code>domains.""</code>, add{" "}
                      <code>"loginKey": "&lt;64-hex-chars&gt;"</code> if you want SSO from
                      AeroGauge. Generate with{" "}
                      <code>
                        python -c 'import secrets; print(secrets.token_hex(32))'
                      </code>
                      .
                    </li>
                    <li>
                      Allow agent install (default is on). Optionally create a device group
                      per customer.
                    </li>
                  </ul>
                </li>
                <li>
                  Open <em>My Devices → Add Device</em> in MeshCentral. Copy the install
                  one-liner — you'll paste it into AeroGauge Customer config below.
                </li>
              </Bullet>

              <H3>Per-customer config in AeroGauge</H3>
              <Bullet>
                <li>
                  Go to <Path>Customer detail → Remote Desktop</Path> tab. Fill:
                  <ul className="list-disc list-inside ml-4 mt-1">
                    <li>
                      <strong>MeshCentral URL</strong> — your server's public URL.
                    </li>
                    <li>
                      <strong>Device group</strong> (optional) — the mesh ID from MeshCentral.
                    </li>
                    <li>
                      <strong>Install command</strong> — paste from "Add Device" dialog.
                    </li>
                    <li>
                      <strong>SSO user + HMAC key</strong> (optional) — the user AeroGauge logs
                      in as, plus the hex key you added to <code>loginKey</code>.
                    </li>
                  </ul>
                </li>
                <li>
                  Save. The MeshAgent install command is now ready for techs to push to
                  endpoints.
                </li>
              </Bullet>

              <H3>Deploying MeshAgent + linking devices</H3>
              <Bullet>
                <li>
                  On any device's detail page, an admin can call{" "}
                  <code>POST /api/v1/devices/&#123;id&#125;/install-meshagent</code> (UI button
                  coming) which queues the customer's install command via{" "}
                  <code>install_meshagent</code> AgentCommand.
                </li>
                <li>
                  After MeshAgent reports in, copy the node ID from MeshCentral's UI and PUT
                  it to <code>/api/v1/devices/&#123;id&#125;/mesh-link</code> so the Remote
                  Desktop button has somewhere to go.
                </li>
                <li>
                  The Remote Desktop button on Device detail then opens MeshCentral's desktop
                  view in a new tab with the device pre-selected.
                </li>
              </Bullet>

              <H3>Modes</H3>
              <Bullet>
                <li>
                  <strong>sso</strong> — URL has HMAC params; user is logged in automatically.
                </li>
                <li>
                  <strong>manual</strong> — URL points at the device, no auth params. User logs
                  in to MeshCentral once, then is redirected.
                </li>
                <li>
                  <strong>not_provisioned</strong> — device has no <code>mesh_node_id</code>{" "}
                  set; button warns and links to the MeshCentral dashboard instead.
                </li>
              </Bullet>
            </HSection>

            <HSection
              id="ha-shell-broker"
              title="HA shell broker (Redis)"
              group="Reference"
              icon="🔁"
            >
              <p>
                The Device Shell feature uses an in-memory broker by default — fine for a
                single uvicorn process, but sessions die on restart and can't be shared across
                replicas. Setting <code>REDIS_URL</code> in <code>.env</code> swaps in a
                Redis-backed broker that survives single-instance restarts and works across
                horizontally-scaled deployments.
              </p>

              <H3>Enabling</H3>
              <Bullet>
                <li>
                  Run Redis somewhere reachable from the backend host. For local dev:{" "}
                  <code>sudo apt install redis-server</code> then{" "}
                  <code>sudo systemctl start redis</code>. For prod consider a managed service
                  (ElastiCache, Memorystore, etc.).
                </li>
                <li>
                  Add <code>REDIS_URL=redis://host:6379/0</code> to <code>.env</code>. Use{" "}
                  <code>rediss://</code> for TLS. Restart the backend.
                </li>
                <li>
                  Verify: hit a shell session — backend log will show{" "}
                  <code>shell broker (redis): paired …</code> instead of{" "}
                  <code>shell broker: paired …</code>. <code>/api/v1/shell/stats</code>{" "}
                  reports <code>{`{"backend": "redis"}`}</code>.
                </li>
              </Bullet>

              <H3>How it works</H3>
              <Bullet>
                <li>
                  Two Redis lists per session: <code>shell:&#123;token&#125;:to_admin</code>{" "}
                  and <code>shell:&#123;token&#125;:to_agent</code>. The peers RPUSH outbound
                  frames; BLPOP drains inbound.
                </li>
                <li>
                  Pairing handshake uses a Redis SET plus a pub/sub channel{" "}
                  <code>shell:paired:&#123;token&#125;</code> — the parker subscribes BEFORE
                  adding to the SET so no joiner publish is missed.
                </li>
                <li>
                  Sliding 30-min EXPIRE on every push; no background sweeper needed. Stale
                  sessions disappear when both peers stop typing.
                </li>
                <li>
                  Frames are length-encoded with a 1-byte type prefix (binary vs text) so
                  control messages (resize) and raw PTY bytes share the same channel without
                  corruption.
                </li>
              </Bullet>

              <H3>Falling back</H3>
              <p>
                Unset <code>REDIS_URL</code> and restart — the broker reverts to in-memory. No
                schema or config migration needed; the two backends present an identical
                interface to the rest of the app.
              </p>
            </HSection>

            <HSection
              id="ai-compliance"
              title="Compliance assessments"
              group="Reference"
              icon="📋"
            >
              <p>
                Sidebar → <strong>Reports &amp; compliance → Compliance</strong>. The AI scores the current posture
                against a regulatory framework (HIPAA, SOX, GLBA, SOC 2, PCI DSS, NIST CSF, CIS)
                and returns a control-by-control verdict: <em>met / partial / gap / unknown</em>{" "}
                with a one-line evidence note and a suggested next step.
              </p>

              <H3>What the AI sees</H3>
              <Bullet>
                <li>Total device count + how many are currently online.</li>
                <li>Active user count + MFA coverage; <strong>privileged-user MFA</strong> coverage separately.</li>
                <li>Audit-log activity over the last 30 days + <strong>actual log retention</strong> (days).</li>
                <li><strong>BitLocker</strong> coverage (escrowed volumes / total) — encryption-at-rest evidence.</li>
                <li><strong>Backup coverage</strong> (devices with backup activity in the last 14 days).</li>
                <li><strong>Critical patch compliance</strong> (devices with no pending critical patches).</li>
                <li>Open alerts, and how many critical alerts have been open ≥ 7 days.</li>
                <li>The framework's built-in control catalog (15–18 controls per framework).</li>
                <li>Optional customer scope — restrict the snapshot to one customer.</li>
                <li>Optional focus instruction ("focus on PHI handling", "Q3 audit prep").</li>
              </Bullet>

              <H3>Framework reports (auditor handoff)</H3>
              <p>
                Alongside the AI-generated assessment, the Compliance page now offers a
                <strong> rule-based framework report</strong> — a deterministic PDF (or CSV)
                that maps each framework control to AeroGauge evidence behind it. Same posture
                always produces the same report, which is what auditors want.
              </p>
              <Bullet>
                <li>
                  Click <strong>Download framework report (PDF)</strong> or the{" "}
                  <strong>CSV</strong> button on the Compliance page (next to "Run
                  assessment"). Choose framework + customer scope first.
                </li>
                <li>
                  The PDF includes: (1) AeroGauge evidence inputs snapshot, (2) per-control
                  status (met / partial / gap / unknown) with the threshold rule that
                  produced it, (3) a next-step hint for each control.
                </li>
                <li>
                  Direct API access for automation:{" "}
                  <code>GET /api/v1/compliance/framework-report.pdf?framework=hipaa&amp;customer_id=N</code>{" "}
                  and the matching <code>.csv</code> / <code>.json</code> variants.
                </li>
                <li>
                  Controls with no automatic signal stay marked <strong>unknown</strong>{" "}
                  with the prompt "manual evidence required" — attach the policy doc /
                  training log / SOC report yourself for those.
                </li>
              </Bullet>

              <H3>What the AI does NOT do</H3>
              <Bullet>
                <li>It does <strong>not</strong> replace a real audit. Treat the score as triage.</li>
                <li>
                  It does <strong>not</strong> auto-remediate. Gaps surface in the UI; you decide
                  what to do next.
                </li>
                <li>
                  It does <strong>not</strong> have a memory of past assessments — each run is
                  independent.
                </li>
              </Bullet>

              <H3>Enabling</H3>
              <Bullet>
                <li>AI Settings → toggle <strong>Compliance assessments</strong>.</li>
                <li>Master <code>ai_enabled</code> must be on, and at least one provider active.</li>
                <li>
                  Every run records an <code>AIAction</code> with{" "}
                  <code>action_type=compliance_assess</code>, plus an audit-log row.
                </li>
              </Bullet>

              <H3>Frameworks covered</H3>
              <Bullet>
                <li>HIPAA Security Rule (15 controls).</li>
                <li>SOX IT General Controls (15 controls).</li>
                <li>GLBA Safeguards Rule (15 controls).</li>
                <li>SOC 2 Common Criteria (16 controls).</li>
                <li>PCI DSS v4.0 (12 requirements).</li>
                <li>NIST Cybersecurity Framework v2.0 (15 categories).</li>
                <li>CIS Critical Security Controls v8 (top 18).</li>
              </Bullet>
            </HSection>

            <HSection
              id="meraki-licenses"
              title="Network vendor licence expiry alerts"
              group="Reference"
              icon="📡"
            >
              <p>
                AeroGauge watches licence and support-contract expiry across
                every customer and tells you before something lapses. Two
                sources feed it, and they behave the same once they are in:
              </p>
              <Bullet>
                <li>
                  <strong>Pulled automatically</strong> from a network
                  controller that exposes a licence API. Configure the
                  integration under <Path>Discovery &amp; integrations</Path>{" "}
                  and the daily sync picks it up.
                </li>
                <li>
                  <strong>Entered by hand</strong> for anything else — a
                  firewall subscription, a backup licence, a support contract.
                  Open the customer, then the <strong>Licences</strong> card,
                  and record vendor, product, seat count, annual cost, renewal
                  contact and expiry date.
                </li>
              </Bullet>

              <H3>What happens as expiry approaches</H3>
              <p>One daily sweep covers both sources and does three things:</p>
              <Bullet>
                <li>
                  An <Path>Alert</Path> with metric{" "}
                  <code>license_expiring</code>: <strong>warning</strong> at
                  &le;&nbsp;30 days, <strong>critical</strong> at &le;&nbsp;7
                  days and after expiry.
                </li>
                <li>
                  A <Path>Calendar</Path> entry on the expiry day with a
                  30-day reminder, one copy per active admin, so the existing
                  reminder-email job fires when it lands.
                </li>
                <li>
                  A <strong>renewal ticket</strong> on that customer's queue
                  at high priority, naming the vendor, product, expiry and
                  days remaining. Replies CC your own helpdesk inbox, not the
                  customer-facing support address. The licence row remembers
                  the ticket, so the next sweep updates it instead of opening
                  a second one.
                </li>
              </Bullet>
              <p>
                Renew, and the next sweep sees the new date, clears the
                30-day/7-day debounce and the ticket link, and lets the cycle
                start again if the new term is short. The alert drops back to{" "}
                <strong>warning</strong>, or resolves outright once expiry is
                more than 30 days away.
              </p>

              <H3>Fleet-wide view</H3>
              <p>
                Sidebar → <Path>Network → Licence renewals</Path>. Every
                expiring licence across every customer, grouped by severity
                (expired / &le;&nbsp;7d / &le;&nbsp;30d / beyond) with KPI
                tiles above. The window defaults to 30 days and stretches to a
                year for renewal planning. Each row links to the customer and
                to the calendar entry, and it honours demo mode — with demo
                off, only real customers appear.
              </p>
              <p>
                Both sources appear here, tagged <strong>synced</strong> or{" "}
                <strong>manual</strong> so you can tell at a glance whether a
                row fixes itself on the next sync or needs the date editing by
                hand. Rows with a renewal ticket link straight to it.
              </p>

              <H3>Licensing models, for the pulled kind</H3>
              <Bullet>
                <li>
                  <strong>Per-device</strong> — one row per licence, bound to a
                  device serial.
                </li>
                <li>
                  <strong>Co-termination</strong> — a single row carrying the
                  org-wide expiry date. The adapter detects which model the
                  organisation uses from the first response and falls back on
                  its own.
                </li>
              </Bullet>

              <H3>Setup</H3>
              <Steps>
                <li>
                  For the pulled kind: add the controller integration for that
                  customer under <Path>Discovery &amp; integrations</Path> —
                  vendor, API key, enabled. The daily job picks it up the next
                  morning; to pull straight away open the customer's licences
                  tab and use <strong>Sync now</strong> (admin only).
                </li>
                <li>
                  For everything else: add the licence by hand on the
                  customer's <strong>Licences</strong> card. There is nothing
                  to sync — the expiry date you typed is what the sweep reads.
                </li>
                <li>
                  Anything inside 30 days alerts on the next sweep (6:00 AM ET
                  daily, or immediately after a manual sync).
                </li>
              </Steps>

              <H3>What it does not do</H3>
              <Bullet>
                <li>
                  No email to the customer's own contact — the reminder is
                  in-app plus your renewal ticket.
                </li>
                <li>
                  No automatic purchase. Renewal is a commercial act and
                  AeroGauge does not attempt it; it makes sure a human is
                  holding a ticket in time to do it.
                </li>
              </Bullet>
            </HSection>

            <HSection
              id="demo-mode"
              title="Demo mode (sales / training)"
              group="Reference"
              icon="🎬"
            >
              <p>
                A one-click toggle that turns the live appliance into a safe
                presentation surface. The console still shows the real customer
                data so you can demo the actual product — but anything that would
                <strong> reach out beyond the appliance</strong> is blocked while
                the toggle is ON.
              </p>

              <H3>Where the toggle lives</H3>
              <p>
                <strong>Left sidebar, right under the logo</strong> — the first
                thing anyone sees when the console loads. The tile reads
                <strong> "Demo mode · OFF"</strong> when off; clicking flips it to
                a yellow <strong>"DEMO MODE ON"</strong> tile, plus a yellow banner
                appears across the very top of every screen. Admin-only toggle —
                techs see the tile (read-only) when an admin has demo mode on, so
                they know not to expect outbound side effects to fire.
              </p>

              <H3>What gets blocked while demo mode is on</H3>
              <Bullet>
                <li>Compliance evidence email (<code>POST /api/v1/compliance/email</code>) — 423 response.</li>
                <li>QBR email (<code>POST /api/v1/qbr/email</code>) — 423 response.</li>
                <li>Intune privileged operations: wipe, retire, lock, reboot — 423 response before the Graph call.</li>
              </Bullet>
              <p>
                Every blocked call returns HTTP 423 (Locked) with the message
                "Demo mode is ON — &lt;action&gt; is disabled…" so the operator
                sees an unambiguous toast in the UI instead of a silent no-op.
              </p>

              <H3>What deliberately still works</H3>
              <Bullet>
                <li>Reading data — you want the demo to look real.</li>
                <li>Local DB writes (creating tickets, contracts, opportunities, time entries) — typing real-looking input is part of a good demo.</li>
                <li>The AI Actions approval queue still runs, since the destructive call sites it leads to are individually guarded. A propose-then-approve flow that ends in an outbound email will fail at the email step, not silently succeed.</li>
              </Bullet>

              <H3>Demo-data view filter</H3>
              <p>
                Customers tagged <code>is_demo = true</code> (and all their
                cascading data — devices, tickets, alerts, opportunities,
                contracts, invoices, time entries) are <strong>hidden while demo
                mode is OFF</strong> and revealed when it's ON. Nothing is deleted;
                toggling ON brings the demo records straight back. This keeps your
                seeded demo customers out of the way during real day-to-day work,
                then available the moment you present.
              </p>
              <p>
                The filter reaches the rollups too: the <strong>dashboard tiles</strong>{" "}
                (total customers / devices / online / offline / pending reboot /
                open tickets), the <strong>"SLA — at risk or breached"</strong>{" "}
                widget, the <strong>"What's broken right now"</strong> snapshot, and
                the <strong>capacity-forecast</strong> list all count and list only
                visible customers. So with demo mode OFF you won't see demo devices
                or demo customers' tickets inflating the dashboard.
              </p>

              <H3>Persistence</H3>
              <p>
                The flag lives in <code>app_settings.demo_mode</code> and is read
                by <code>GET /api/v1/settings/branding/public</code> — so the
                banner is visible on logged-out screens too. It survives appliance
                restarts; remember to toggle it back OFF when you're done.
              </p>

              <H3>Adding new guards</H3>
              <p>
                When a future endpoint touches an outbound surface (sends an
                email, hits a customer's tenant with a privileged write, etc.),
                add a single line at the top of the handler:
              </p>
              <code className="block bg-slate-900 text-emerald-300 p-3 my-2 text-xs rounded-lg font-mono shadow-inner">
                from services.demo_mode import raise_if_demo_mode<br />
                raise_if_demo_mode(db, action_label="sending the X email")
              </code>
              <p>
                That's it — the helper handles the 423 response and the message.
                Grep <code>raise_if_demo_mode</code> to see all current guard
                points.
              </p>
            </HSection>

            <HSection
              id="mobile-dashboard-push"
              title="Mobile dashboard & push notifications"
              group="Reference"
              icon="📲"
            >
              <p>
                Two things that only make sense once AeroGauge is on a phone. For
                getting it there, see{" "}
                <a href="#mobile-pwa" className="text-brand dark:text-brand-light hover:underline">
                  Install as a mobile app (PWA)
                </a>
                .
              </p>

              <H3>Mobile dashboard</H3>
              <p>
                Sidebar → <Path>AI → Mobile dashboard</Path> (also available as a
                manifest shortcut — long-press the home-screen icon and pick{" "}
                <strong>On-call</strong>). One-screen view designed for narrow
                viewports: critical alerts, your assigned tickets, recent devices.
                Auto-refreshes every 60 seconds. Includes an offline indicator
                that lights up when <code>navigator.onLine</code> goes false.
              </p>

              <H3>Push notifications</H3>
              <p>
                Settings → <Path>Push Notifications</Path>. Subscribe once per
                installed device; the appliance pushes critical alerts +
                AI-action-needs-approval events to the OS notification tray. Works
                in installed-PWA mode and in the regular browser tab.
              </p>
            </HSection>

            <HSection
              id="psa-module"
              title="PSA — Contracts, Invoices, Pipeline"
              group="Reference"
              icon="💼"
            >
              <p>
                Native PSA built into AeroGauge: per-customer contracts drive the
                billing engine, time entries roll up to draft invoices, and a lightweight
                sales pipeline tracks opportunities. No ConnectWise / Autotask / HaloPSA
                integration — all in-app.
              </p>

              <H3>Contracts</H3>
              <p>
                Sidebar → <Path>PSA → Contracts</Path>. One contract per (customer,
                commercial agreement). Types:
              </p>
              <Bullet>
                <li>
                  <strong>MSP block</strong> — monthly retainer + N included hours, T&amp;M
                  on overage at the contract's hourly rate.
                </li>
                <li>
                  <strong>MSP unlimited</strong> — flat monthly retainer, no hour cap.
                  Time entries appear as $0 informational lines on the invoice so the
                  customer sees the value delivered.
                </li>
                <li>
                  <strong>Time &amp; materials</strong> — pure T&amp;M at the contract's
                  hourly rate. Time entries roll up grouped by ticket.
                </li>
                <li>
                  <strong>Fixed-price project</strong> — single project-total line.
                  Operators can add milestone adjustments to the draft invoice before
                  finalizing.
                </li>
              </Bullet>
              <p>
                Contracts can optionally link an <code>SLAPolicy</code> for display next
                to the contract terms. The SLA engine still reads from{" "}
                <code>sla_policies</code> directly — this link is for audit + display.
              </p>

              <H3>Invoices</H3>
              <p>
                Sidebar → <Path>PSA → Invoices</Path>. Click <strong>Generate
                invoice</strong>, pick the customer + period (defaults to the current
                month). The billing engine:
              </p>
              <Bullet>
                <li>Resolves the active contract for the period start (MSP block beats unlimited beats T&amp;M beats project on overlap).</li>
                <li>Pulls billable, completed time entries in the period.</li>
                <li>Emits line items per the contract type's rule (retainer + overage / per-ticket T&amp;M / etc.).</li>
                <li>Saves as <code>DRAFT</code> — you can still edit + add adjustment / materials lines.</li>
              </Bullet>
              <p>
                Flow: <strong>DRAFT → Finalize → SENT → Mark paid → PAID</strong>.
                Finalizing allocates an invoice number (format <code>INV-YYYY-NNNN</code>)
                and stamps issue / due dates (net-30 default). Once SENT, line items
                freeze — adjustments after that have to be a new invoice.
              </p>
              <p>
                Open any invoice and use <strong>Download PDF</strong> for a branded PDF
                (your company name, colour and support email come from the branding
                settings), or <strong>Email invoice</strong> to send that PDF to the
                customer's <code>billing_email</code> (falls back to{" "}
                <code>contact_email</code>). Emailing a draft finalizes it first, so an
                invoice can never go out without a number. Sending is blocked in demo mode
                and written to the audit log.
              </p>

              <H3>Rate cards</H3>
              <p>
                Sidebar → <Path>PSA → Rate cards</Path>. A rate card is a set of
                <em> role → hourly-rate</em> rows. Cards are either <strong>global</strong>{" "}
                (the house schedule) or <strong>customer-specific</strong> (overrides the
                global card for one customer). When a tech logs time with a{" "}
                <code>role</code> (e.g. "Tier 1", "After-hours"), the matched rate is{" "}
                <em>snapshotted</em> onto the time entry — editing a card later never
                restates already-logged time. T&amp;M invoices bill each entry at its
                snapshot rate, splitting a ticket into separate lines when work spanned
                more than one rate. With no rate cards configured, billing falls back to
                the customer's flat hourly rate exactly as before.
              </p>

              <H3>Payments &amp; AR aging</H3>
              <p>
                On a finalized invoice, record <strong>partial or full payments</strong>{" "}
                (method, reference, amount). The invoice flips to <code>PAID</code> only
                once payments cover the total; a negative payment (refund / chargeback)
                re-opens it to <code>SENT</code>. Sidebar →{" "}
                <Path>PSA → AR aging</Path> buckets every outstanding balance by days past
                due (<code>current / 1–30 / 31–60 / 61–90 / 90+</code>) with per-customer
                and per-invoice breakdowns. The aging report honours the demo-mode customer
                filter.
              </p>

              <H3>Recurring invoicing</H3>
              <p>
                On a contract (block / unlimited / T&amp;M), tick{" "}
                <strong>Auto-generate monthly invoices</strong> and set a{" "}
                <strong>billing day of month</strong>. A daily sweep bills the prior
                calendar month once that day arrives, guarded against double-billing.
                Leave it as a draft for review, or tick{" "}
                <strong>Finalize &amp; email automatically</strong> to send it straight to
                the customer. Drive it by hand for testing from{" "}
                <code>POST /api/v1/psa/recurring/run</code> (<code>preview:true</code>{" "}
                lists what would bill without creating anything).
              </p>

              <H3>Sales pipeline</H3>
              <p>
                Sidebar → <Path>PSA → Pipeline</Path>. Five stages:{" "}
                <code>lead → qualified → proposal → won / lost</code>. Each opportunity
                has expected value × probability % = weighted pipeline. The kanban
                summary across the top shows open pipeline, weighted total, and won
                lifetime.
              </p>
              <Bullet>
                <li>Opportunities can be for existing customers OR free-text prospects (no customer record yet).</li>
                <li>Stage transitions log to an append-only activity timeline.</li>
                <li>Moving to <code>won</code> auto-stamps probability=100% and actual_close_date=today; <code>lost</code> sets probability=0%.</li>
              </Bullet>

              <H3>What's not in yet</H3>
              <Bullet>
                <li>Stripe / payment-processor integration — payments are recorded by the operator (manual entry of check / ACH / card receipts), not pulled from a gateway.</li>
                <li>Customer-portal PSA-invoice viewer (the <code>can_view_invoices</code> flag exists; the portal page is still wired against the legacy billing report, not PSA invoices).</li>
                <li>Quote / estimate → invoice conversion off a won opportunity.</li>
              </Bullet>
            </HSection>

            <HSection
              id="intune-devices"
              title="Intune device admin"
              group="Reference"
              icon="📱"
            >
              <p>
                Sidebar → <strong>AI → Intune devices</strong>. Manage a customer's
                Intune-enrolled Windows / macOS / iOS / Android fleet via Microsoft Graph,
                reusing the customer's existing M365 tenant config (no separate tenant
                registration).
              </p>
              <H3>Setup (once per customer)</H3>
              <Steps>
                <li>
                  In the customer's Azure AD admin, open the app registration that already
                  powers their M365 integration (the one configured in{" "}
                  <Path>Customers → &lt;customer&gt; → M365</Path>).
                </li>
                <li>
                  Add these <strong>Application</strong> Graph permissions:
                  <Bullet>
                    <li><code>DeviceManagementManagedDevices.Read.All</code> — read fleet + compliance</li>
                    <li><code>DeviceManagementManagedDevices.PrivilegedOperations.All</code> — wipe / retire / reboot / lock</li>
                  </Bullet>
                </li>
                <li>Grant admin consent. Wait ~5 minutes for propagation.</li>
                <li>
                  In AeroGauge, open <Path>Intune devices</Path>, pick the customer, click{" "}
                  <strong>Load devices</strong>. The fleet appears.
                </li>
              </Steps>

              <H3>What you can do per device</H3>
              <Bullet>
                <li>
                  <strong>Sync</strong> — force a check-in. Low risk; devices auto-sync every
                  ~8h anyway, this just hurries it along. Admin / technician.
                </li>
                <li>
                  <strong>Lock</strong> — instant remote lock. Mobile platforms lock
                  immediately; Windows requires a compatible policy.
                </li>
                <li>
                  <strong>Reboot</strong> — Windows / macOS reboot now. User loses unsaved
                  work.
                </li>
                <li>
                  <strong>Retire</strong> — un-enroll from Intune without factory reset.
                  Corporate apps + work profile removed; personal data kept. Use this when
                  an employee leaves and you're handing the device back.
                </li>
                <li>
                  <strong>Wipe</strong> — full factory reset. Data loss.
                </li>
              </Bullet>

              <H3>AI-driven Intune actions</H3>
              <p>
                The AI surface mirrors the cloud / M365 patterns: writes go through the
                AI Actions approval queue, never auto-execute. Action types:{" "}
                <code>intune_sync_device</code> (MEDIUM),{" "}
                <code>intune_reboot_device</code> /{" "}
                <code>intune_remote_lock_device</code> /{" "}
                <code>intune_retire_device</code> /{" "}
                <code>intune_wipe_device</code> (all HIGH risk).
              </p>
              <code className="block bg-slate-900 text-emerald-300 p-3 my-2 text-xs rounded-lg font-mono shadow-inner whitespace-pre-wrap">
                claude "Wipe Jane's laptop in Acme's Intune — she lost it and Find My Device
                shows it as offline since yesterday."
              </code>
            </HSection>

            <HSection
              id="ai-terminal"
              title="AI Terminal (in-app shell)"
              group="Reference"
              icon="💬"
            >
              <p>
                Sidebar → <strong>AI Terminal</strong> (admin-only). Opens a bash PTY{" "}
                <em>on AeroGauge appliance</em> (not on a managed device) in your browser. Each
                session gets a fresh workspace at{" "}
                <code>&lt;appliance&gt;/ai-workspaces/&lt;token&gt;/</code> with a pre-staged{" "}
                <code>CLAUDE.md</code> that tells the Claude CLI (or any AI tool you run inside)
                it's operating inside AeroGauge.
              </p>

              <H3>What's preset in the shell</H3>
              <Bullet>
                <li>
                  <code>RMM_API_URL</code> — the local backend, typically{" "}
                  <code>http://127.0.0.1:8000</code>.
                </li>
                <li>
                  <code>RMM_USER</code> — your username (the operator who opened the terminal).
                </li>
                <li>
                  <code>RMM_WORKSPACE</code> — the session's scratch dir; auto-<code>cd</code>'d on
                  start.
                </li>
                <li>
                  <code>CLAUDE.md</code> in the workspace explains what the AI can and cannot do
                  here.
                </li>
                <li>
                  A welcome banner with a hint at the top of the session ("Try:{" "}
                  <code>claude "summarize the open critical alerts"</code>").
                </li>
              </Bullet>

              <H3>Calling AeroGauge API from inside the shell</H3>
              <Bullet>
                <li>
                  We deliberately don't inject your browser JWT. Mint a long-lived API token
                  under <Path>Integrations → Inbound API access</Path>, then export it:
                </li>
                <li>
                  <code>export RMM_API_TOKEN=ag_…</code>
                </li>
                <li>
                  Then anything inside the shell — including Claude — can call the API:{" "}
                  <code>
                    curl -fsS -H "Authorization: Bearer $RMM_API_TOKEN"
                    $RMM_API_URL/api/v1/alerts
                  </code>
                </li>
              </Bullet>

              <H3>Leaving the page doesn't end the session</H3>
              <p>
                Click away to check an alert and come back — the shell is still there, still
                running, with the screen you left. The page re-attaches to your session rather
                than opening a new one, and the server replays the last of the output so you
                can see where you were; a <strong>Resumed</strong> badge appears next to the
                status when it does. Anything that was running while you were away kept
                running, and its output is waiting for you.
              </p>
              <Bullet>
                <li>
                  <strong>New session</strong> deliberately starts a clean one; use it when you
                  want a fresh workspace rather than the conversation you had.
                </li>
                <li>
                  <strong>End &amp; close</strong> ends the session for real and takes you out.
                  Typing <code>exit</code> in the shell ends it too.
                </li>
                <li>
                  A terminal already open in <em>another tab</em> is left alone — you get a new
                  session rather than having the screen yanked out from under the other tab.
                </li>
              </Bullet>

              <H3>Limits &amp; lifecycle</H3>
              <Bullet>
                <li>Admin-only at the router; non-admins don't see the sidebar entry.</li>
                <li>
                  Max 3 concurrent sessions per user. Returning to the page reuses your
                  session rather than spending another slot.
                </li>
                <li>
                  Idle reaper at 30 min. "Idle" means no typing <em>and</em> no output, so a
                  long job you left running does not count against it — but a session sitting
                  at a prompt with nobody on it is reclaimed.
                </li>
                <li>
                  Every open + explicit close lands in the audit log under{" "}
                  <code>ai_terminal_open</code> / <code>ai_terminal_close</code>.
                </li>
                <li>
                  Workspaces persist on disk, so files you created survive even after the
                  shell behind them is gone.
                </li>
              </Bullet>

              <H3>Security notes</H3>
              <Bullet>
                <li>
                  The shell runs as the backend user. Anyone with admin role effectively has
                  shell access to the appliance — same as the device shell + scripts features.
                </li>
                <li>Don't write secrets to files in the workspace; it's on disk.</li>
                <li>
                  Destructive actions (reboot, patch, AD changes) should still flow through{" "}
                  <code>/api/v1/ai/actions</code> so the approval gates apply — the CLAUDE.md
                  inside the workspace tells Claude this.
                </li>
              </Bullet>
            </HSection>

            <HSection
              id="network-discovery"
              title="Network discovery & auto-onboarding"
              group="Reference"
              icon="🔍"
            >
              <p>
                Sidebar → <strong>Network Discovery</strong>. Define one or more CIDRs per customer; an
                agent on that customer's LAN sweeps them on a schedule and reports everything it finds back to the
                server. The discovery list aggregates results from <em>both</em> agent scans <em>and</em> vendor API
                imports (see "Network vendor API integrations" below), so you have a single pane of "what's on this
                customer's network".
              </p>

              <H3>How a scan runs</H3>
              <Bullet>
                <li>
                  You define a <strong>scan target</strong>: a CIDR (e.g. <code>192.168.1.0/24</code>), a schedule
                  (default: daily), and optional knobs — light TCP port scan, SNMP autodetect.
                </li>
                <li>
                  Pick a <strong>scanner device</strong> (an always-on managed endpoint on the customer's LAN), or let
                  the server auto-pick the first online agent at scan time.
                </li>
                <li>
                  The server queues a <code>network_discover</code> agent command. The agent runs ICMP ping fan-out
                  (defaults to 64 parallel), reads the ARP cache for MACs, does reverse-DNS for hostnames, optionally
                  TCP-pokes a short port list on responders, and optionally tries SNMPv2c with common community strings
                  to flag managed network gear.
                </li>
                <li>
                  Results POST back to <code>/api/v1/agent/network-discovery/results</code> and land in the
                  <code>discovered_devices</code> table. Each host is classified — Windows / Linux / network gear /
                  printer / IoT / unknown — so the action button on the row matches what to do next.
                </li>
              </Bullet>

              <H3>Acting on a discovery</H3>
              <Bullet>
                <li>
                  <strong>Unmanaged Windows / Linux:</strong> tick the row and click "Push installer". The server
                  queues an <code>install_agent_remote</code> command on the customer's runner device (the existing
                  AD-runner mechanism); the runner PSRemotes the bootstrap one-liner out to each target. Targets
                  appear as managed devices within ~5 min.
                </li>
                <li>
                  <strong>Network gear (SNMP-responsive switches/firewalls/UPS):</strong> click "Promote to SNMP" to
                  create a <code>NetworkDevice</code> row. The existing SNMP poller starts watching it.
                </li>
                <li>
                  <strong>Already managed / already SNMP:</strong> linked automatically by hostname. The action panel
                  hides — nothing to do.
                </li>
                <li>
                  <strong>Irrelevant (printer test page, guest phone, AppleTV):</strong> click "Ignore" with a reason.
                  Ignored rows hide by default; unignore from the filter.
                </li>
              </Bullet>

              <H3>Safe defaults</H3>
              <Bullet>
                <li>
                  CIDRs larger than <code>/16</code> (65k hosts) are rejected — break big ranges into smaller targets.
                </li>
                <li>
                  Per-host timeouts: 300 ms ICMP, 600 ms TCP, 800 ms SNMP. A typical <code>/24</code> sweep completes
                  in &lt; 60 s.
                </li>
                <li>
                  Port-scan is <strong>off by default</strong> — pure ICMP/ARP discovery is much quieter to
                  customer IDS / IPS. Turn it on per target if you actually need port info.
                </li>
              </Bullet>
            </HSection>

            <HSection
              id="network-integrations"
              title="Network vendor API integrations"
              group="Configure"
              icon="🔌"
            >
              <p>
                Sidebar → <strong>Network Discovery → Vendor API integrations</strong>. Pulls device inventories from
                vendor APIs and merges them into the same discovery list as agent scans. Use this when the customer's
                network gear is cloud-managed (Meraki, Aruba Central, etc.) so you don't need on-prem SNMP visibility,
                or when you want a second source to cross-check agent scans.
              </p>

              <H3>Supported vendors (v1)</H3>
              <Bullet>
                <li><strong>Cisco Meraki</strong> — Dashboard API key, organization scope.</li>
                <li><strong>Ubiquiti UniFi Network</strong> — controller URL + admin login (UniFi OS or legacy).</li>
                <li><strong>Ubiquiti UniFi Protect</strong> — same controller, camera/NVR inventory.</li>
                <li><strong>Fortinet FortiGate</strong> — REST API token, per VDOM.</li>
                <li><strong>SonicWall (SonicOS)</strong> — admin user + password, SonicOS API enabled.</li>
                <li><strong>HPE Aruba Central</strong> — OAuth bearer token, regional API endpoint.</li>
                <li><strong>Palo Alto PAN-OS</strong> — XML API key, ARP table import.</li>
                <li><strong>MikroTik RouterOS</strong> — REST API on v7+, basic auth.</li>
                <li><strong>Sophos Central</strong> — API token + tenant ID.</li>
                <li><strong>WatchGuard Cloud</strong> — API key + account ID.</li>
              </Bullet>

              <H3>How it works</H3>
              <Bullet>
                <li>
                  Each integration is <em>per customer</em>. API keys/tokens are encrypted at rest with the same
                  AES-256-GCM master key as the AI provider configs.
                </li>
                <li>
                  Click <strong>Test</strong> to probe the vendor without saving devices. Click <strong>Sync now</strong>
                  to pull current inventory; vendor-imported rows land in the same Discovered devices list with
                  <code>device_class = "network_gear"</code>.
                </li>
                <li>
                  Custom <code>extra_config</code> per vendor: org ID for Meraki, VDOM for FortiGate, site for UniFi,
                  etc. The form surfaces only the fields the chosen vendor needs.
                </li>
                <li>
                  Periodic sync schedule: configured per integration (default 60 min). Scheduler hook arrives in v2 —
                  for now, hit "Sync now" or schedule via the existing scheduler config.
                </li>
              </Bullet>

              <H3>Adding a new vendor</H3>
              <p>
                Drop a new adapter module under <code>services/network_integrations/</code> with
                <code>probe(api_key, base_url, extra)</code> and <code>list_devices(api_key, base_url, extra)</code>.
                Register the module name in <code>KNOWN_ADAPTERS</code> and add the kind to
                <code>VENDOR_KINDS</code>. Every adapter returns the same normalized record shape
                (<code>ip_address</code>, <code>hostname</code>, <code>mac_address</code>, <code>model</code>,
                <code>serial</code>, <code>firmware</code>, <code>status</code>), so the discovery list stays clean.
              </p>
            </HSection>

            <HSection
              id="deployment-onprem-offprem"
              title="On-prem vs off-prem server"
              group="Configure"
              icon="🌐"
            >
              <p>
                AeroGauge server can live <strong>on the customer's LAN</strong> (your shared MSP appliance hosted in
                their datacenter or HQ rack) or <strong>off-prem</strong> (your central MSP appliance hosted in your
                NOC or cloud). The agent doesn't care which — it just calls the server URL it was bootstrapped with.
              </p>

              <H3>What changes between the two</H3>
              <Bullet>
                <li>
                  <strong>Server URL the agent calls.</strong> Bootstrap token bakes
                  <code>SERVER_URL</code> into <code>agent.cfg</code> at install. On-prem typically
                  <code>https://rmm.lan.customer.local</code>; off-prem typically
                  <code>https://rmm.your-msp.com</code>. Both work; both encrypt.
                </li>
                <li>
                  <strong>TLS.</strong> Off-prem needs a real cert (Let's Encrypt or your CA). On-prem can use an
                  internal CA; push the CA root via GPO so the agent trusts it.
                </li>
                <li>
                  <strong>Firewall rules.</strong> Off-prem: customer firewall allows outbound HTTPS to your server.
                  On-prem: nothing leaves the LAN unless the customer wants you to push reports/alerts to your central
                  PSA.
                </li>
                <li>
                  <strong>Mail.</strong> Identical either way: the appliance sends its own mail
                  directly, DKIM-signed, from addresses derived from its own subdomain. No provider
                  account and no relay, so there is nothing to differ between on-prem and off-prem.
                  What each site DOES need is outbound port 25 reachable, plus inbound 25 if it is
                  to receive support email.
                </li>
                <li>
                  <strong>Discovery scanner.</strong> The agent designated as scanner needs L2 reach to the CIDR
                  (ICMP/ARP work only on-link). Off-prem servers can still drive a scan — they just send the command
                  to a customer-side agent.
                </li>
              </Bullet>

              <H3>Picking which model</H3>
              <Bullet>
                <li>
                  <strong>Single-tenant heavy customer:</strong> on-prem appliance keeps their data local + survives
                  their internet outages.
                </li>
                <li>
                  <strong>Multi-customer MSP:</strong> off-prem central server is the default. One server, many
                  customers, one place to look. Branded per-customer via the existing branding feature.
                </li>
                <li>
                  <strong>Compliance-driven:</strong> some HIPAA / financial customers require data residency in
                  their network — on-prem appliance wins. The Compliance feature surfaces this in the readiness
                  scorecard.
                </li>
              </Bullet>
            </HSection>

            <HSection
              id="custom-dashboards"
              title="Custom dashboards"
              group="Reference"
              icon="📊"
            >
              <p>
                Sidebar → <strong>My Dashboards</strong>. Each user gets their own dashboards — pick which widgets you
                want, in what order, and the page renders only those. The built-in Dashboard is still there if you
                prefer the curated view.
              </p>

              <H3>Building one</H3>
              <Bullet>
                <li>First visit auto-creates a starter dashboard with the most useful widgets pre-loaded.</li>
                <li>
                  Click <strong>Edit</strong> to enter layout mode → <strong>+ Add widget</strong> for the picker. Each
                  widget describes what it shows; you can have at most one of each on a dashboard.
                </li>
                <li>Use ↑ / ↓ on a card to reorder. ✕ removes a widget from the layout.</li>
                <li>Click <strong>Save layout</strong> to persist changes — until then your edits live only in the tab.</li>
                <li>
                  Create multiple dashboards via <strong>New</strong>. Mark one as default via the API
                  (<code>is_default=true</code>) to make it the one that opens first.
                </li>
              </Bullet>

              <H3>Available widgets (v1)</H3>
              <Bullet>
                <li>Open alerts count, critical alerts open, devices offline, total devices.</li>
                <li>Open tickets (optionally just yours), SLA-breached tickets, tickets by priority bar chart.</li>
                <li>Patch compliance %, AI actions pending approval, discovered unmanaged endpoints.</li>
                <li>Recent tickets list, recent open alerts list.</li>
                <li>Alerts per day (30-day sparkline).</li>
              </Bullet>

              <H3>Adding a new widget type</H3>
              <p>
                Edit <code>services/dashboard_widgets.py</code>. Append to <code>WIDGETS</code> with{" "}
                <code>compute(db, user, params)</code> returning <code>{`{"value": int, "label": str}`}</code> for
                numbers, <code>{`{"items": [...]}`}</code> for lists, <code>{`{"series": [...]}`}</code> for
                sparklines. The widget picker in the UI picks up new entries automatically.
              </p>
            </HSection>

            <HSection
              id="mobile-pwa"
              title="Install as a mobile app (PWA)"
              group="How do I…"
              icon="📱"
            >
              <p>
                AeroGauge is a Progressive Web App — you can install it on a phone, tablet, or desktop so it lives in
                your launcher / dock alongside native apps. No app-store review, no separate build.
              </p>

              <H3>Installing on iPhone / iPad (Safari)</H3>
              <Bullet>
                <li>Open AeroGauge in Safari (not Chrome — Apple disables install for third-party browsers).</li>
                <li>Tap the <strong>Share</strong> button → <strong>Add to Home Screen</strong>.</li>
                <li>Confirm the name and tap <strong>Add</strong>. The icon lands on your home screen.</li>
              </Bullet>

              <H3>Installing on Android (Chrome) / Desktop (Chrome / Edge)</H3>
              <Bullet>
                <li>
                  Look for the <strong>⬇ Install app</strong> link in the top-right of the page, or use the
                  browser's "Install AeroGauge" prompt in the URL bar.
                </li>
                <li>
                  If the prompt doesn't appear: Chrome only fires <code>beforeinstallprompt</code> once per
                  visit and only over HTTPS. Hit refresh, or use the browser menu's "Install app" item.
                </li>
              </Bullet>

              <H3>What works offline</H3>
              <Bullet>
                <li>
                  The app shell (HTML/JS/CSS) is precached by the service worker — installed PWAs can launch even
                  when the network is unreachable.
                </li>
                <li>
                  API calls (<code>/api/*</code>) deliberately bypass the cache. An offline PWA shows the login
                  shell and surfaces a connection error if you try to load tickets. We do this on purpose — a stale
                  ticket list could mislead a tech responding to an incident.
                </li>
                <li>
                  Push notifications keep working when the PWA is closed — same service worker as the in-browser
                  push subscription.
                </li>
              </Bullet>

              <H3>Updating the installed PWA</H3>
              <p>
                The service worker auto-updates in the background. When you redeploy, the new shell takes effect on
                the next launch (or after a tab refresh). To force-update right now: open the menu → "Reload" twice,
                or uninstall + reinstall.
              </p>
            </HSection>

            <HSection
              id="roles-access"
              title="Roles & access"
              group="Configure"
              icon="🚦"
              intro="Turn individual features on and off per role."
            >
              <p>
                <strong>Admin</strong>, <strong>Technician</strong> and <strong>Viewer</strong> are
                coarse. Most MSPs have a technician they would rather keep out of Licensing, or a
                viewer who genuinely does need the reports screen. <Path>Admin → Roles &amp; access</Path>{" "}
                is a feature-by-role grid for exactly that.
              </p>
              <Steps>
                <div>
                  Open <Path>Admin → Roles &amp; access</Path>.
                </div>
                <div>Toggle a feature off for Technician or Viewer. Changed cells are ringed.</div>
                <div>
                  <strong>Save</strong>. People already signed in see it on their next page load.
                </div>
              </Steps>

              <H3>What switching something off actually does</H3>
              <Bullet>
                <li>
                  It disappears from that role's navigation, so nobody clicks through to a
                  permission error.
                </li>
                <li>
                  The server refuses the request too, so typing the URL does not get around it.
                </li>
              </Bullet>

              <Note>
                <strong>This only ever takes access away.</strong> It does not grant anything. The
                permission checks on each individual action are unchanged and are still what stops a
                viewer writing — this layer sits on top and narrows.
              </Note>

              <Tip>
                Administrators always have every feature and that column cannot be edited.
                Otherwise the last administrator could switch off the very screen they would need to
                switch it back on.
              </Tip>
            </HSection>

            <HSection
              id="passwords"
              title="Password policy"
              group="Configure"
              icon="🔑"
              intro="Generate one, or type one — the same rules either way."
            >
              <p>
                Anywhere the product creates an account or resets a password, you can take a
                generated password or set one yourself. Generated is the default and usually the
                right answer; typing one is for standing next to a new starter and handing over
                something they can actually type once.
              </p>

              <H3>The rules</H3>
              <Bullet>
                <li>At least 12 characters.</li>
                <li>Three of: lower case, upper case, a digit, a symbol.</li>
                <li>Not an obvious password, and not one containing the account name.</li>
              </Bullet>

              <Note>
                The browser checks as you type so the reason is immediate, but{" "}
                <strong>the server is the control</strong> and re-checks every time. Both Active
                Directory and the cloud directory also reject weak passwords themselves — checking
                here turns a vague failure two minutes later into a specific message now.
              </Note>

              <Tip>
                A typed password is never echoed back on the confirmation screen. You already have
                it, and showing it again just puts it somewhere else.
              </Tip>
            </HSection>

            <HSection
              id="scheduled-reports"
              title="Scheduled reports"
              group="Configure"
              icon="⏰"
              intro="Any report, any cadence, to any address."
            >
              <Steps>
                <div>
                  <Path>Reports &amp; compliance → Scheduled reports</Path>.
                </div>
                <div>Pick a report — every runnable report and generated document is listed.</div>
                <div>Choose daily, weekly or monthly, and the hour.</div>
                <div>Add the recipients. They do not need a login.</div>
              </Steps>

              <Note>
                <strong>The hour is local, not UTC.</strong> It uses the timezone set under{" "}
                <Path>Admin → Settings</Path>, and the schedule stores that zone rather than a
                converted hour — so 9am stays 9am when the clocks change instead of drifting by an
                hour twice a year.
              </Note>
            </HSection>


            <HSection
              id="sessions"
              title="Sessions & idle sign-out"
              group="Reference"
              icon="⏳"
              intro="One session per person, and what happens when they walk away."
            >
              <Bullet>
                <li>
                  <strong>One active session per user.</strong> Signing in somewhere else ends the
                  first session — the other device bounces to the login screen within about a
                  minute rather than sitting there looking signed in.
                </li>
                <li>
                  <strong>Sign-in origin is recorded</strong> — where and when, visible on the user
                  row, which is how you spot a shared login.
                </li>
                <li>
                  <strong>Idle sign-out</strong> is set appliance-wide under{" "}
                  <Path>Admin → Users</Path> and can be overridden per person. It measures real
                  keyboard and mouse activity, so a background refresh does not count as being at
                  the desk, and it is enforced on the server — the session genuinely ends rather
                  than being hidden.
                </li>
              </Bullet>
            </HSection>

            <HSection
              id="ai-spend"
              title="AI spend limits"
              group="Configure"
              icon="💷"
              intro="A monthly ceiling, platform-wide and per customer."
            >
              <p>
                Set a monthly budget under <Path>AI → AI Settings</Path>, and optionally a
                per-customer sub-budget. You get a warning at 80% before anything stops.
              </p>
              <Note>
                <strong>Automatic AI is rationed before interactive AI.</strong> Background work —
                ticket triage, alert summarisation — gives way first, so a nightly job that burns
                the budget cannot leave a technician without the assistant at two in the morning.
              </Note>
              <Tip>
                If the AI provider is your own account, this ceiling is protecting your bill rather
                than ours. It is worth setting on day one.
              </Tip>
            </HSection>

            <HSection
              id="alert-digest"
              title="Alert noise & the AI digest"
              group="Configure"
              icon="🔕"
              intro="Stop the flood without stopping the signal."
            >
              <p>
                Left alone, a fleet this size produces well over a thousand alerts a week, and the
                great majority are a handful of repeating checks. Three settings deal with it, under{" "}
                <Path>Monitoring &amp; Alerts → Settings</Path>:
              </p>
              <Bullet>
                <li>
                  <strong>Severity floor</strong> — below this, alerts are recorded but not emailed.
                </li>
                <li>
                  <strong>Repeat suppression</strong> — the same alert on the same device stops
                  re-sending for a window.
                </li>
                <li>
                  <strong>Digest</strong> — one grouped email on a schedule instead of one per
                  event, with related alerts collapsed into a single incident and a plain-English
                  summary at the top.
                </li>
              </Bullet>
              <Tip>
                Alerts are still all there on the Alerts screen. This changes what reaches your
                inbox, not what is monitored.
              </Tip>
            </HSection>

            <HSection
              id="ticket-tools"
              title="Ticket tools"
              group="Reference"
              icon="🧰"
              intro="Merge, internal notes, attachments and canned replies."
            >
              <Bullet>
                <li>
                  <strong>Merge</strong> duplicates into one ticket. The losing ticket is kept, so
                  an old ticket number still resolves for anyone who quotes it.
                </li>
                <li>
                  <strong>Internal notes</strong> are staff-only and never appear in the customer
                  portal. They do not stamp the SLA clock and they notify separately.
                </li>
                <li>
                  <strong>Attachments</strong> on both the staff and portal side. Type is decided
                  from the file, not from what the browser claims, and the size cap is enforced
                  while the upload streams rather than after it lands.
                </li>
                <li>
                  <strong>Canned replies</strong> substitute placeholders server-side. A placeholder
                  that cannot be filled falls back to neutral wording rather than leaving a hole in
                  the message.
                </li>
              </Bullet>
              <Note>
                Ticket status always moves through the status service, which is what keeps the
                active-time and SLA clocks honest. A status changed any other way leaves the clock
                running forever.
              </Note>
            </HSection>

            <HSection
              id="portal-activity"
              title="Portal activity log"
              group="Reference"
              icon="📜"
              intro="What the customer can see about who touched their environment."
            >
              <p>
                Portal administrators get an <strong>Activity</strong> screen listing changes to
                their own environment — directory and tenant changes, device actions, and remote
                sessions onto their machines.
              </p>
              <Note>
                It is deliberately narrower than the staff audit log. Entries are scoped to that one
                customer, the detail payload is never included, and anything naming credentials,
                keys, or commercial arrangements is excluded. New kinds of event stay hidden until
                somebody classifies them, which is the safe direction to fail in.
              </Note>
              <Tip>
                Remote sessions <em>are</em> shown, on purpose. "Your provider opened a session on
                ADSVR01 at 15:04" is exactly the transparency an audit trail is for, and clients
                react well to it.
              </Tip>
            </HSection>

            <HSection
              id="wf-software-coverage"
              title="Find machines missing an app"
              group="How do I…"
              icon="🧭"
              intro="The inverse of the inventory screen."
            >
              <Steps>
                <div>
                  <Path>Devices → Software → Coverage</Path>.
                </div>
                <div>
                  Type the application name and pick <strong>Anything matching "…"</strong> — that
                  asks about the app rather than one specific build.
                </div>
                <div>
                  Read the three counts: <strong>Missing</strong>, <strong>Installed</strong>,{" "}
                  <strong>Not inventoried</strong>.
                </div>
                <div>Deploy to the missing list if a matching package exists in the library.</div>
              </Steps>

              <Note>
                <strong>"Missing" and "not inventoried" are different things.</strong> A machine
                only counts as missing if it has reported a software inventory that does not contain
                the app. One that has never reported an inventory might well have it, so it is kept
                out of the deploy list — pushing an installer at a machine you have not inventoried
                is the thing that reinstalls over a working copy.
              </Note>

              <Tip>
                Vendors version their display names — "7-Zip" is nine different strings across
                builds and architectures. The screen tells you which ones it matched so you can
                check the number rather than trust it.
              </Tip>
            </HSection>

            <HSection
              id="wf-portal-documents"
              title="Give a client their own reports"
              group="How do I…"
              icon="📂"
            >
              <p>
                Portal administrators can pull three documents for their own company from{" "}
                <Path>Portal → Reports</Path>: the quarterly business review, the plain-English
                assessment, and the device inventory. Nothing to set up — they appear for anyone
                with the portal administrator role.
              </p>
              <Note>
                The compliance framework report and the evidence pack are deliberately not on that
                list. They carry your internal control assessments and gap notes, which is working
                material <em>about</em> the client rather than a deliverable <em>to</em> them.
              </Note>
              <Tip>
                Everything a portal user pulls is scoped to their own company by the server, not by
                the request — there is no way to ask for someone else's copy.
              </Tip>
            </HSection>

            <HSection
              id="wf-unlock-ad"
              title="Unlock a locked-out account"
              group="How do I…"
              icon="🔓"
            >
              <Steps>
                <div>
                  <Path>Identity &amp; Tools → Active Directory</Path>, find the account — locked
                  ones carry an amber badge.
                </div>
                <div>
                  <strong>Unlock</strong>. The on-site agent applies it within a minute or two.
                </div>
              </Steps>
              <Tip>
                Portal administrators can do this for themselves now, which takes the most common
                call off your queue entirely. It is the safest self-service action there is — it
                grants no access the account did not already have.
              </Tip>
              <Note>
                It needs the customer's AD runner on agent 1.0.31 or newer. An older agent accepts
                the command and silently does nothing, so the product refuses up front rather than
                pretending.
              </Note>
            </HSection>

            <HSection id="wf-offboard" title="Offboard a leaver" group="How do I…" icon="👋">
              <Steps>
                <div>Find the person under Active Directory or Microsoft 365.</div>
                <div>
                  <strong>Offboard</strong>, and read the confirmation — it lists exactly what is
                  about to happen.
                </div>
                <div>Check the result message, which reports each step separately.</div>
              </Steps>

              <H3>What it does</H3>
              <Bullet>
                <li>
                  <strong>Cloud account:</strong> blocks sign-in, signs them out everywhere, clears
                  their MFA methods, and <strong>releases their licences</strong>.
                </li>
                <li>
                  <strong>Domain account:</strong> disables it, resets the password, revokes
                  sessions and strips group memberships.
                </li>
              </Bullet>

              <Tip>
                The licence release is the part with a monthly bill attached. A leaver who is
                disabled but still licensed carries on costing money, quietly, which is why the
                result message says how many seats came back.
              </Tip>

              <Note>
                If a step fails, the message says which one. A partial offboard reported as a clean
                one is how a seat goes on being billed for a year.
              </Note>
            </HSection>

            <HSection id="troubleshooting" title="Troubleshooting" group="Help" icon="🛠️">
              <H3>"A device isn't showing up after install"</H3>
              <Bullet>
                <li>Wait 60 seconds — first heartbeat may be in flight.</li>
                <li>
                  On the endpoint, check the agent service is running (<code>Services.msc</code> →
                  look for "AeroGauge RMM Agent").
                </li>
                <li>Check the endpoint can reach the server URL (try the URL in a browser).</li>
                <li>Check Windows Firewall isn't blocking outbound 443.</li>
              </Bullet>

              <H3>"A device says online but metrics are stale"</H3>
              <Bullet>
                <li>Metrics update every 60s on heartbeat; inventory every ~1 hour.</li>
                <li>
                  If a metric is hours old, the agent isn't completing its collection cycle —
                  check the device's Event Log, look at the agent service log file.
                </li>
              </Bullet>

              <H3>"I locked myself out"</H3>
              <Bullet>
                <li>
                  5 failed password attempts in 15 minutes → 30-min lockout. Wait it out, or ask
                  an admin to unlock you from the Users page.
                </li>
                <li>
                  Lost MFA + backup codes? Only an admin can disable MFA on your account from the
                  Users page.
                </li>
              </Bullet>

              <H3>"A webhook stopped firing"</H3>
              <Bullet>
                <li>
                  Settings → Webhooks → check the "Last status" column for the failing endpoint.
                  Click <strong>Test</strong> to fire a synthetic event and see the actual error.
                </li>
                <li>
                  For Slack: the most common cause is a deleted or disabled incoming webhook on
                  their side.
                </li>
                <li>For PagerDuty: 401/403 usually means the integration key was rotated.</li>
              </Bullet>

              <H3>"A scheduled report didn't send"</H3>
              <Bullet>
                <li>
                  Open the saved report → Schedules. Check <strong>Last run status</strong> and{" "}
                  <strong>Last run error</strong>.
                </li>
                <li>Common: recipient address invalid, schedule disabled, or outbound port 25 blocked at the site. <Path>HelpDesk → Mail setup</Path> reports the outbound DNS + queue state.</li>
              </Bullet>

              <H3>"Patches won't install"</H3>
              <Bullet>
                <li>
                  Confirm they're <em>approved</em> on Patch Compliance.
                </li>
                <li>Confirm the current time is inside the customer's maintenance window.</li>
                <li>Check the device's Patches tab for the most recent attempt's error.</li>
              </Bullet>
            </HSection>

            <HSection id="shortcuts" title="Tips & shortcuts" group="Help" icon="✨">
              <Bullet>
                <li>Click the logo / "Dashboard" to jump home.</li>
                <li>URLs are bookmarkable — share a device or ticket URL with a colleague.</li>
                <li>Sidebar footer has light/dark mode.</li>
                <li>
                  Reports can be scheduled to email a customer weekly — set it once, never touch
                  it again.
                </li>
                <li>Every customer-facing email action shows a confirm modal with recipient override.</li>
                <li>
                  Audit Log CSV export is unfiltered by row count — use the filter row first to
                  narrow.
                </li>
                <li>Bulk device actions cap at 500 per request.</li>
                <li>
                  This Help guide is rewritten automatically as features change — re-open it
                  after upgrades.
                </li>
              </Bullet>
            </HSection>

            <footer className="mt-10 pt-6 border-t border-slate-200 dark:border-slate-700 text-center">
              <div className="text-xs text-slate-400 dark:text-slate-500 mb-1">
                Help & How-To Guide <span className="font-mono">v3</span>
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400 font-semibold">
                Auto-synced with feature changes
              </div>
            </footer>
          </div>
        </main>
      </div>
    </div>
  );
}
